'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Invitation, Registerer, Session, SessionByeOptions, UserAgent } from 'sip.js';
import { fetchJson } from '@/lib/api/client';
import { sipApi } from '@/lib/api/sip';
import {
  ANSWER_LOOP_MESSAGE,
  applyPreferredSink,
  classifyMediaError,
  hasAudioInputDevice,
  MICROPHONE_DENIED_MESSAGE,
  NO_MICROPHONE_MESSAGE,
  optionalMediaDevices,
  optionalPermissions,
  resolveAudioConstraints,
  writePreferredInputDeviceId,
  writePreferredOutputDeviceId,
} from '@/lib/audio-devices';
import {
  autoAnswerAbortReason,
  classifyInviteLeg,
  isInviteBusy,
  type AutoAnswerAbortReason,
} from '@/lib/sip-invite-classification';
import {
  callLinkQualityBetween,
  connectionQualitySignature,
  rateConnection,
  readCallLinkCounters,
  registrationDownSince,
  registrationVerdict,
  reportableRegistrationClock,
  type CallLinkCounters,
  type CallLinkQuality,
  type ConnectionQuality,
  type RegistrationSnapshot,
} from '@/lib/connection-quality';
import {
  createGenericTelemetryEventQueue,
  type GenericTelemetryEventQueue,
} from '@/lib/telemetry-event-queue';
import {
  canClaimSoftphoneLease,
  clearSoftphoneOwnerLease,
  getSoftphoneTabId,
  openSoftphoneTabBus,
  readSoftphoneOwnerLease,
  SOFTPHONE_OWNER_RENEW_MS,
  SOFTPHONE_OWNER_STORAGE_KEY,
  SOFTPHONE_OWNER_TTL_MS,
  writeSoftphoneOwnerLease,
  type SoftphoneOwnerLease,
  type SoftphoneTabBus,
  type SoftphoneTabCommand,
  type SoftphoneTabSnapshot,
} from '@/lib/softphone-tab-sync';
import { useContactByPhone } from '@/hooks/use-contact-by-phone';
import { reloadIfChunkLoadError, setChunkReloadLiveCall } from '@/hooks/use-version-skew';
import { swapRegisterer } from '@/lib/softphone-registerer';
import { byeOptionsFor } from '@/lib/pool-call-rescue';
import { liveConversationCall } from '@/lib/softphone-status';
import { isPooledAgentLine } from '@/lib/call-utils';
import {
  assessMediaPath,
  IDLE_MEDIA_REBUILD_EPISODE,
  MEDIA_REBUILD_BRIDGED_REAP_WAIT_MS,
  MEDIA_REBUILD_DEFER_RECHECK_MS,
  MEDIA_REBUILD_REAP_WAIT_MS,
  MEDIA_REBUILD_SEAT_READ_TIMEOUT_MS,
  MEDIA_STUCK_PROBE_MS,
  assessMediaFlow,
  mediaRebuildConfirmMs,
  mediaRebuildDeferralExpired,
  nextMediaRebuild,
  type MediaFlowVerdict,
  type MediaRebuildEpisode,
  type MediaRebuildSeat,
} from '@/lib/media-rebuild';
import { runMediaRebuild } from '@/lib/media-rebuild-run';
import {
  assessUplink,
  endUplinkWatch,
  IDLE_SILENT_UPLINK_WATCH,
  SILENT_UPLINK_TICK_MS,
  uplinkOnHold,
  type SilentUplinkBlock,
  type SilentUplinkWatch,
} from '@/lib/silent-uplink';
import { pooledDialerApi } from '@/lib/api/pooled-dialer';
import {
  hasPhoneRuntime,
  memoizeLoad,
  softphoneRetryDelayMs,
  SOFTPHONE_REGISTER_TIMEOUT_MS,
  withTimeout,
} from '@/lib/softphone-start';
import { isAgentRole, type UserRole } from '@/types/common';
import type {
  CallSummary,
  SoftphoneCredentialsResponse,
  SoftphoneIncomingCall,
  SoftphoneState,
  TelephonyCallMetricSampleInput,
  TelephonyNetworkSnapshot,
  TelephonyTraceConfig,
} from '@/types/telephony';

// CRLF keepalive on the SIP WebSocket (RFC 5626). Between registration
// refreshes the socket is otherwise silent, so a half-dead link on a mobile
// or CGNAT line only surfaced when FreeSWITCH's OPTIONS ping gave up on it;
// regular traffic keeps the NAT mapping alive and turns a dead socket into a
// client-side close the reconnect path can act on.
const SIP_KEEPALIVE_INTERVAL_S = 20;
// Floor between pool-driven re-REGISTER nudges (reRegister below): the pool
// re-asks on every membership snapshot while its seat waits.
const REREGISTER_NUDGE_MIN_INTERVAL_MS = 5000;
const CONNECTION_QUALITY_TICK_MS = 5000;
const IDLE_CONNECTION_QUALITY: ConnectionQuality = {
  level: 'good',
  rttMs: null,
  downlinkMbps: null,
  call: null,
  reconnects: 0,
  registrationDown: false,
  registrationRetrying: false,
};
// Rebuild margin before forced-relay credentials expire (16h TTL server-side);
// generous so a sleeping laptop that misses timers still wakes inside it.
const RELAY_CREDENTIAL_REFRESH_MARGIN_MS = 2 * 60 * 60 * 1000;
// In-place reconnect tunables (NET-227).
const RECONNECT_IN_PLACE_ATTEMPTS = 5;
const RECONNECT_IN_PLACE_DELAY_MS = 2000;
// How long ICE may sit in `disconnected` before we force an ICE-restart
// re-INVITE. `disconnected` routinely self-heals within seconds on lossy
// links; restarting early spends a signaling round trip (and its failure
// modes) on a blip the media path would have survived on its own.
const ICE_DISCONNECT_GRACE_MS = 8000;
// Minimum gap between ICE-restart re-INVITEs so a flapping link can't spam them.
const ICE_RESTART_COOLDOWN_MS = 8000;
// Telemetry events ship in batches, never one POST per event: each request's
// TLS + header overhead races voice RTP for the same congested uplink, and
// connection-state churn multiplies events exactly when the link is worst
// (measured: 5,531 telemetry requests in one setter's 3.5h shift). The queue
// flushes on the interval below, at the batch threshold, immediately for
// error events, and via sendBeacon on pagehide/session stop.
const TELEMETRY_EVENT_FLUSH_MS = 15_000;
// Batch threshold and backlog caps live in lib/telemetry-event-queue.ts,
// shared with the app-wide client telemetry pipeline.
// Deploys close the stream when the old backend exits; Caddy holds the
// reconnect attempt until the new backend listens, so a short delay here
// directly bounds how long call tiles stay stale during a deploy.
const STREAM_RECONNECT_MS = 1000;
const STREAM_FALLBACK_POLL_MS = 2500;
const STREAM_RECONCILE_POLL_MS = 5000;
const STREAM_ACTIVE_RECONCILE_POLL_MS = 1000;
// The stream is the sole source of call state while healthy: full-state
// snapshots self-heal a missed event on the next push, and the server emits a
// `ping` event every 15s so silence is observable. The reconcile timers below
// only actually fetch once nothing (calls or ping) has arrived for this long —
// a healthy stream means zero steady-state polling.
const STREAM_STALE_AFTER_MS = 35_000;
// status=tracked mirrors the stream payload (active + recently-ended tracked
// calls), so fallback polling feeds the same consumers — including the ended
// rows the dialer's outcome classifier reads.
const ACTIVE_CALL_SNAPSHOT_PATH = '/api/sip/calls?limit=40&status=tracked';
// Lease keys/TTL and the cross-tab bus live in softphone-tab-sync.ts.
// Owner-side lease writes are also driven by SSE stream events (which Chrome
// never throttles) — this floor just keeps the interval-driven writes from
// spamming storage events at every stream push during an active call.
const OWNER_LEASE_MIN_WRITE_GAP_MS = 4_000;
// Make-before-break lease handoff: after losing the runtime lease, the old
// tab keeps its (still-registered) runtime alive until the winning tab
// reports its phone online — the winner's REGISTER atomically replaces our
// binding (single-registration FreeSWITCH profile), so the extension is never
// unregistered during a tab switch. The cap bounds how long a stuck winner
// can keep two runtimes open.
const LEASE_HANDOFF_MAX_MS = 20_000;
// Written by a finishing lame-duck tab right before it closes its socket; the
// lease owner responds with a fresh REGISTER. Covers the rare case where the
// loser's periodic sip.js refresh re-took the binding mid-handoff.
const SOFTPHONE_REASSERT_KEY = 'sales-suite:softphone-reassert';
// Softphone credentials only change on admin edits; reusing the last fetch
// keeps a runtime rebuild (tab switch, reconnect) off the network. A
// background revalidate still picks up rotations promptly.
const CREDENTIALS_CACHE_TTL_MS = 5 * 60_000;
// While a sticky device fault is set, recovery is re-probed on this heartbeat
// (busy-mic release has no push event). Only runs during a fault.
const DEVICE_FAULT_REPROBE_MS = 15_000;
// A generic (unknown-cause) answer-loop trip ignores recovery probes for this
// long, bounding a persistent non-device fault to ~3 burned dials per minute
// instead of one per second.
const GENERIC_FAULT_COOLDOWN_MS = 60_000;
// Consecutive auto-answer failures of unknown cause before the sticky
// breaker trips anyway.
const ANSWER_LOOP_TRIP_THRESHOLD = 3;
const TONE_FREQ_A = 440;
const TONE_FREQ_B = 480;

type SoftphoneToneKind = 'ringback' | 'ring';

type TonePattern = {
  bursts: Array<{ atMs: number; durMs: number }>;
  cycleMs: number;
  gain: number;
};

const TONE_PATTERNS: Record<SoftphoneToneKind, TonePattern> = {
  // Outbound ringback: one long quiet burst per cycle, heard while dialing.
  ringback: { bursts: [{ atMs: 0, durMs: 1000 }], cycleMs: 4000, gain: 0.03 },
  // Incoming ring: double burst and louder, so it reads as "pick up" rather than "dialing".
  ring: { bursts: [{ atMs: 0, durMs: 450 }, { atMs: 650, durMs: 450 }], cycleMs: 3000, gain: 0.08 },
};

export type TelemetrySessionQueuedEvent = {
  eventName: string;
  severity: 'info' | 'warning' | 'error';
  callId: string | null;
  payload: Record<string, unknown>;
  clientAt: string;
};

export type TelemetryEventQueue = GenericTelemetryEventQueue<TelemetrySessionQueuedEvent>;

// Queue/flush semantics live in lib/telemetry-event-queue.ts (shared with the
// app-wide client telemetry pipeline); this binds the softphone event shape
// and its counted dropped-events marker.
export function createTelemetryEventQueue(
  send: (events: TelemetrySessionQueuedEvent[]) => Promise<boolean>
): TelemetryEventQueue {
  return createGenericTelemetryEventQueue<TelemetrySessionQueuedEvent>(send, (count) => ({
    eventName: 'telemetry_events_dropped',
    severity: 'warning',
    callId: null,
    payload: { count },
    clientAt: new Date().toISOString(),
  }));
}

export type CallStreamDeltaPayload = {
  seq: number;
  changed: CallSummary[];
  removed: string[];
};

export type CallStreamMirror = {
  applyFull: (calls: CallSummary[], seq?: number) => void;
  applyDelta: (payload: CallStreamDeltaPayload) => { ok: boolean; calls?: CallSummary[] };
  toSortedCalls: () => CallSummary[];
};

// Client half of the delta stream protocol (?proto=delta): mirrors the
// server's per-connection visible call set so a "delta" event (changed call
// objects + removed ids) can be expanded back into the full array that
// applyCallSnapshot expects. Pure and exported for tests — same pattern as
// createTelemetryEventQueue. Instances live only in the stream effect's
// closure/refs (render-churn history, PR #476); unchanged calls keep their
// object identity across delta applies, which is strictly gentler on the
// downstream fingerprint guards than a full-array replacement.
export function createCallStreamMirror(): CallStreamMirror {
  const byId = new Map<string, CallSummary>();
  let seq = 0;

  // Same ordering as server full snapshots (updatedAt desc), so a
  // delta-expanded array and a full snapshot are interchangeable downstream.
  const toSortedCalls = (): CallSummary[] =>
    [...byId.values()].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));

  return {
    applyFull: (calls, nextSeq) => {
      byId.clear();
      for (const callItem of calls) {
        byId.set(callItem.callId, callItem);
      }
      if (typeof nextSeq === 'number') {
        // Stream snapshots carry the server's per-connection seq and
        // re-baseline it; fallback-poll refreshes carry none and leave the
        // counter alone (the poll rebaselines content, not sequencing).
        seq = nextSeq;
      }
    },
    applyDelta: (payload) => {
      if (payload.seq !== seq + 1) {
        // SSE over one connection is ordered and lossless — a gap can only be
        // an implementation bug. Report poison; the caller full-refreshes via
        // the existing pollCallsOnce path and the server's 60s resync
        // re-baselines the sequence.
        return { ok: false };
      }
      for (const callItem of payload.changed) {
        byId.set(callItem.callId, callItem);
      }
      for (const callId of payload.removed) {
        byId.delete(callId);
      }
      seq = payload.seq;
      return { ok: true, calls: toSortedCalls() };
    },
    toSortedCalls,
  };
}

// sip.js is dynamically imported; memoize the import so runtime rebuilds
// (tab switches, reconnects) never wait on the chunk again. memoizeLoad drops
// a rejected load so a failed chunk fetch doesn't wedge the phone until reload.
const loadSipModule = memoizeLoad(() => import('sip.js'));

function credentialsMateriallyDiffer(a: SoftphoneCredentialsResponse, b: SoftphoneCredentialsResponse): boolean {
  return a.enabled !== b.enabled
    || a.sipUsername !== b.sipUsername
    || a.sipPassword !== b.sipPassword
    || a.sipDomain !== b.sipDomain
    || a.websocketUrl !== b.websocketUrl
    || (a.extension || null) !== (b.extension || null)
    || Boolean(a.forceRelay) !== Boolean(b.forceRelay);
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function parseRemoteIdentity(session: Session): string {
  // sip.js types displayName as string but returns undefined when the From
  // header has no display name — an unguarded .trim() here threw inside
  // onInvite's first statement and silently killed every auto-answered
  // auto-answer (the a-leg INVITE carried no caller-ID name).
  const displayName = typeof session.remoteIdentity.displayName === 'string' ? session.remoteIdentity.displayName.trim() : '';
  if (displayName) {
    return displayName;
  }
  const user = session.remoteIdentity.uri.user?.trim();
  if (user) {
    return user;
  }
  return 'Unknown caller';
}

function getInviteHeader(session: Session, name: string): string | null {
  const maybeInvite = session as Session & {
    request?: {
      getHeader?: (name: string) => string | undefined;
    };
  };
  const request = maybeInvite.request;
  if (!request || typeof request.getHeader !== 'function') {
    return null;
  }
  return request.getHeader(name) || request.getHeader(name.toLowerCase()) || null;
}

function extractSalesCallHeader(session: Session): string | null {
  return getInviteHeader(session, 'X-Sales-Call-Id');
}

// Set by the inbound DID bridge: marks a customer call to ring (vs an internal
// dialer leg, which carries only X-Sales-Call-Id and is auto-answered).
function isInboundLegInvite(session: Session): boolean {
  return Boolean(getInviteHeader(session, 'X-Sales-Inbound'));
}

// Set by the hot-transfer adapter on the parked booker leg. It still auto-answers as an internal dialer
// leg, but the browser UI treats it as a background handoff leg until the customer is actually bridged.
function extractHotTransferHeader(session: Session): string | null {
  return getInviteHeader(session, 'X-Hot-Transfer-Id');
}

// The DTLS transport's own state, where the browser exposes RTCDtlsTransport.
function readDtlsState(peerConnection: RTCPeerConnection): string | null {
  const transport = peerConnection.getSenders().find((sender) => sender.transport)?.transport
    ?? peerConnection.getReceivers().find((receiver) => receiver.transport)?.transport;
  return transport?.state ?? null;
}

// Inbound RTP as getStats counts it; null with no inbound-rtp entry yet or a
// connection that can no longer report. getStats is bounded: the watch stays
// fired while the probe is out, so a call that never settles would leave the
// line unjudged for good.
async function readInboundPackets(peerConnection: RTCPeerConnection): Promise<number | null> {
  const counters = await withTimeout(readCallLinkCounters(peerConnection), MEDIA_STUCK_PROBE_MS, 'getStats timed out').catch(() => null);
  return counters?.packetsReceived ?? null;
}

interface MediaFlowProbe {
  verdict: MediaFlowVerdict;
  packetsBefore: number | null;
  packetsAfter: number | null;
}

// Two counter readings MEDIA_STUCK_PROBE_MS apart, judged with the connection
// state at the end (see assessMediaFlow).
async function probeMediaFlow(peerConnection: RTCPeerConnection): Promise<MediaFlowProbe> {
  const packetsBefore = await readInboundPackets(peerConnection);
  await new Promise<void>((resolve) => {
    window.setTimeout(resolve, MEDIA_STUCK_PROBE_MS);
  });
  const packetsAfter = await readInboundPackets(peerConnection);
  return {
    verdict: assessMediaFlow({
      connectionState: peerConnection.connectionState,
      packetsBefore,
      packetsAfter,
    }),
    packetsBefore,
    packetsAfter,
  };
}

// The call's audio transceiver. Found by its receiver, whose track always
// exists: the sender's can be null or replaced.
function audioTransceiver(peerConnection: RTCPeerConnection): RTCRtpTransceiver | null {
  return peerConnection.getTransceivers().find((transceiver) => transceiver.receiver.track.kind === 'audio') ?? null;
}

function rtpCounter(report: RTCStatsReport, type: 'outbound-rtp' | 'inbound-rtp', field: 'packetsSent' | 'packetsReceived'): number | null {
  for (const stat of report.values()) {
    if (stat.type === type) {
      const value = (stat as RTCStats & Partial<Record<typeof field, number>>)[field];
      return typeof value === 'number' ? value : null;
    }
  }
  return null;
}

// Both packet counters of the call's own audio transceiver, so a stale entry
// from an earlier sender or stream on the connection is never read. Bounded
// like the stuck-media probe's reads; null where either side has no entry.
async function readUplinkCounters(transceiver: RTCRtpTransceiver): Promise<{ packetsSent: number; packetsReceived: number } | null> {
  const reports = await withTimeout(
    Promise.all([transceiver.sender.getStats(), transceiver.receiver.getStats()]),
    SILENT_UPLINK_TICK_MS,
    'getStats timed out',
  ).catch(() => null);
  if (!reports) {
    return null;
  }
  const packetsSent = rtpCounter(reports[0], 'outbound-rtp', 'packetsSent');
  const packetsReceived = rtpCounter(reports[1], 'inbound-rtp', 'packetsReceived');
  return packetsSent === null || packetsReceived === null ? null : { packetsSent, packetsReceived };
}

function disabledReasonMessage(reason: string | undefined): string {
  if (reason === 'no_sip_extension_assigned') {
    return 'No SIP extension assigned. Ask admin in Admin SIP.';
  }
  return 'Softphone unavailable for this account.';
}

function normalizePhoneLike(input: string): string {
  return input.replace(/[^\d+]/g, '').trim();
}

// Snapshot payloads arrive freshly parsed (SSE push, 1s in-call reconcile, 5s
// fallback poll), so the picked call is a new object identity every time even
// when nothing changed. Publishing it unconditionally re-renders every
// softphone consumer — the whole dialer workspace — once per poll for an entire
// shift. Compare content instead; both sides come off the same serializer, so
// key order is stable and stringify equality is reliable.
function isSameCallSnapshot(a: CallSummary | null, b: CallSummary | null): boolean {
  if (a === b) {
    return true;
  }
  if (!a || !b) {
    return false;
  }
  return JSON.stringify(a) === JSON.stringify(b);
}

function isSameCall(a: CallSummary | null | undefined, b: CallSummary | null | undefined): boolean {
  if (!a || !b) {
    return false;
  }
  return (
    a.callId === b.callId
    || Boolean(a.callLogId && b.callLogId && a.callLogId === b.callLogId)
    || Boolean(a.interactionId && b.interactionId && a.interactionId === b.interactionId)
  );
}

function pickCurrentUserCall(
  calls: CallSummary[],
  extension: string | null,
  currentCall?: CallSummary | null,
  sessionCallId?: string | null,
): CallSummary | null {
  const active = calls.filter((call) => {
    if (call.status === 'ended') {
      return false;
    }
    if (call.source === 'hot_transfer' || call.source === 'ai_outbound') {
      return call.status === 'bridged';
    }
    return true;
  });
  if (active.length === 0) {
    return null;
  }
  // The live SIP session knows exactly which call its audio belongs to — the
  // X-Sales-Call-Id it was adopted with. Trust it over the heuristics below:
  // the sticky/bridged-first fallbacks can latch onto another tracked call on
  // this extension (e.g. a stale leg the switch still reports), which showed
  // agents the wrong number for the call they were actually on.
  if (sessionCallId) {
    const sessionCall = active.find((call) => call.callId === sessionCallId);
    if (sessionCall) {
      return sessionCall;
    }
    // The session's call isn't in this snapshot yet — at a fast auto-advance
    // the snapshot can lag one call behind for a few seconds (ruth, Jul 7
    // 20:59:52Z: new leg adopted while the previous call was still the only
    // active row, so the fallbacks below displayed the PREVIOUS number for
    // the whole call). While a session is live, showing a different call is
    // always wrong; show none and let the next fresh snapshot fill it in.
    return null;
  }
  if (!extension) {
    return null;
  }
  const byExtension = active.filter((call) => call.agentExtension === extension);
  if (byExtension.length === 0) {
    return null;
  }
  const existing = byExtension.find((call) => isSameCall(call, currentCall));
  if (existing) {
    return existing;
  }
  const bridged = byExtension.find((call) => call.status === 'bridged');
  return bridged ?? byExtension[0];
}

type NetworkInformationLike = {
  effectiveType?: string;
  type?: string;
  rtt?: number;
  downlink?: number;
  addEventListener?: (type: 'change', listener: () => void) => void;
  removeEventListener?: (type: 'change', listener: () => void) => void;
};

function networkInformation(): NetworkInformationLike | undefined {
  const nav = navigator as Navigator & {
    connection?: NetworkInformationLike;
    mozConnection?: NetworkInformationLike;
    webkitConnection?: NetworkInformationLike;
  };
  return nav.connection ?? nav.mozConnection ?? nav.webkitConnection;
}

function readNetworkSnapshot(): TelephonyNetworkSnapshot {
  const connection = networkInformation();
  return {
    online: navigator.onLine,
    effectiveType: typeof connection?.effectiveType === 'string' ? connection.effectiveType : null,
    type: typeof connection?.type === 'string' ? connection.type : null,
    rttMs: typeof connection?.rtt === 'number' && Number.isFinite(connection.rtt) ? Math.max(0, connection.rtt) : null,
    downlinkMbps: typeof connection?.downlink === 'number' && Number.isFinite(connection.downlink) ? Math.max(0, connection.downlink) : null,
  };
}

function roundMetric(value: unknown, digits = 2): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return null;
  }
  const factor = 10 ** digits;
  return Math.round(value * factor) / factor;
}

// oxlint-disable-next-line max-lines-per-function -- frozen baseline (structural-gates rollout)
async function buildWebRtcSample(peerConnection: RTCPeerConnection): Promise<TelephonyCallMetricSampleInput | null> {
  type InboundAudioStats = RTCStats & {
    kind?: string;
    isRemote?: boolean;
    packetsReceived?: number;
    packetsLost?: number;
    bytesReceived?: number;
    jitter?: number;
    audioLevel?: number;
    concealedSamples?: number;
    totalAudioEnergy?: number;
    jitterBufferDelay?: number;
    jitterBufferEmittedCount?: number;
  };
  type OutboundAudioStats = RTCStats & {
    kind?: string;
    isRemote?: boolean;
    packetsSent?: number;
    bytesSent?: number;
    retransmittedPacketsSent?: number;
  };
  type RemoteInboundAudioStats = RTCStats & {
    kind?: string;
    packetsLost?: number;
    roundTripTime?: number;
    jitter?: number;
  };
  type CandidatePairStats = RTCStats & {
    selected?: boolean;
    localCandidateId?: string;
    remoteCandidateId?: string;
    currentRoundTripTime?: number;
    availableOutgoingBitrate?: number;
    availableIncomingBitrate?: number;
    bytesSent?: number;
    bytesReceived?: number;
    requestsReceived?: number;
    responsesReceived?: number;
  };
  type CandidateStats = RTCStats & {
    candidateType?: string;
    protocol?: string;
  };

  const report = await peerConnection.getStats();
  const statsById = new Map<string, RTCStats>();
  report.forEach((stat) => {
    statsById.set(stat.id, stat);
  });

  let inboundAudio: Record<string, unknown> | null = null;
  let outboundAudio: Record<string, unknown> | null = null;
  let remoteInboundAudio: Record<string, unknown> | null = null;
  let candidatePair: RTCStats | null = null;

  for (const stat of report.values()) {
    if (stat.type === 'inbound-rtp' && (stat as InboundAudioStats).kind === 'audio' && !(stat as InboundAudioStats).isRemote) {
      const inbound = stat as InboundAudioStats;
      inboundAudio = {
        packetsReceived: inbound.packetsReceived ?? null,
        packetsLost: inbound.packetsLost ?? null,
        bytesReceived: inbound.bytesReceived ?? null,
        jitterMs: typeof inbound.jitter === 'number' ? roundMetric(inbound.jitter * 1000) : null,
        audioLevel: roundMetric(inbound.audioLevel),
        concealedSamples: inbound.concealedSamples ?? null,
        totalAudioEnergy: roundMetric(inbound.totalAudioEnergy, 4),
        jitterBufferDelayMs:
          typeof inbound.jitterBufferDelay === 'number' && typeof inbound.jitterBufferEmittedCount === 'number' && inbound.jitterBufferEmittedCount > 0
            ? roundMetric((inbound.jitterBufferDelay / inbound.jitterBufferEmittedCount) * 1000)
            : null,
      };
    }
    if (stat.type === 'outbound-rtp' && (stat as OutboundAudioStats).kind === 'audio' && !(stat as OutboundAudioStats).isRemote) {
      const outbound = stat as OutboundAudioStats;
      outboundAudio = {
        packetsSent: outbound.packetsSent ?? null,
        bytesSent: outbound.bytesSent ?? null,
        retransmittedPacketsSent: outbound.retransmittedPacketsSent ?? null,
      };
    }
    if (stat.type === 'remote-inbound-rtp' && (stat as RemoteInboundAudioStats).kind === 'audio') {
      const remoteInbound = stat as RemoteInboundAudioStats;
      remoteInboundAudio = {
        packetsLost: remoteInbound.packetsLost ?? null,
        roundTripTimeMs: typeof remoteInbound.roundTripTime === 'number' ? roundMetric(remoteInbound.roundTripTime * 1000) : null,
        jitterMs: typeof remoteInbound.jitter === 'number' ? roundMetric(remoteInbound.jitter * 1000) : null,
      };
    }
    if (stat.type === 'transport') {
      const transport = stat as RTCTransportStats & { selectedCandidatePairId?: string };
      if (transport.selectedCandidatePairId) {
        candidatePair = statsById.get(transport.selectedCandidatePairId) ?? candidatePair;
      }
    }
    if (stat.type === 'candidate-pair' && (stat as CandidatePairStats).selected) {
      candidatePair = stat;
    }
  }

  if (!inboundAudio && !outboundAudio && !remoteInboundAudio && !candidatePair) {
    return null;
  }

  let selectedCandidate: Record<string, unknown> | null = null;
  if (candidatePair) {
    const pair = candidatePair as CandidatePairStats;
    const local = pair.localCandidateId ? statsById.get(pair.localCandidateId) as CandidateStats | undefined : undefined;
    const remote = pair.remoteCandidateId ? statsById.get(pair.remoteCandidateId) as CandidateStats | undefined : undefined;
    selectedCandidate = {
      localCandidateType: local?.candidateType ?? null,
      localProtocol: local?.protocol ?? null,
      remoteCandidateType: remote?.candidateType ?? null,
      remoteProtocol: remote?.protocol ?? null,
      currentRoundTripTimeMs: typeof pair.currentRoundTripTime === 'number' ? roundMetric(pair.currentRoundTripTime * 1000) : null,
      availableOutgoingBitrateKbps: typeof pair.availableOutgoingBitrate === 'number' ? roundMetric(pair.availableOutgoingBitrate / 1000) : null,
      availableIncomingBitrateKbps: typeof pair.availableIncomingBitrate === 'number' ? roundMetric(pair.availableIncomingBitrate / 1000) : null,
      bytesSent: pair.bytesSent ?? null,
      bytesReceived: pair.bytesReceived ?? null,
      requestsReceived: pair.requestsReceived ?? null,
      responsesReceived: pair.responsesReceived ?? null,
    };
  }

  return {
    recordedAt: new Date().toISOString(),
    legRole: 'internal',
    sampleType: 'webrtc',
    payload: {
      connectionState: peerConnection.connectionState,
      iceConnectionState: peerConnection.iceConnectionState,
      signalingState: peerConnection.signalingState,
      visibilityState: typeof document !== 'undefined' ? document.visibilityState : null,
      network: readNetworkSnapshot(),
      inboundAudio,
      outboundAudio,
      remoteInboundAudio,
      selectedCandidate,
    },
  };
}

// oxlint-disable-next-line max-lines-per-function -- frozen baseline (structural-gates rollout)
export function useSoftphone(role: UserRole) {
  const enabledForRole = isAgentRole(role);
  const [retryNonce, setRetryNonce] = useState(0);
  const [hasRuntimeLease, setHasRuntimeLease] = useState(false);
  // A pool line (auto-answered dialer leg) is established in this tab; a
  // reloaded tab that has one is back on its call and has nothing to claim.
  const [poolLineLive, setPoolLineLive] = useState(false);
  const [primaryDidNumber, setPrimaryDidNumber] = useState<string | null>(null);
  const [dialDestination, setDialDestination] = useState('');
  const [activeCall, setActiveCall] = useState<CallSummary | null>(null);
  // Latest tracked-call snapshot (active + recently-ended, newest first) as
  // pushed by the SSE stream — the single call-state feed for consumers like
  // the dialer's outcome classifier, which reads terminal rows from here
  // instead of refetching call history.
  const [recentCalls, setRecentCalls] = useState<CallSummary[]>([]);
  const [actionError, setActionError] = useState<string | null>(null);
  const [localAudioMuted, setLocalAudioMutedState] = useState(false);
  // NET-227: true while an in-place reconnect is healing the link mid-call, so the
  // widget can show "call still active" without dropping the in-call UI.
  const [reconnecting, setReconnecting] = useState(false);
  // Stuck-media rebuilds ran out (see lib/media-rebuild.ts): the agent's audio
  // is not reaching the switch and only their network can fix it.
  const [mediaFailed, setMediaFailed] = useState(false);
  // The switch is getting no audio from this phone on a bridged call (see
  // lib/silent-uplink.ts). Detect-only: the agent is told, nothing is rebuilt.
  const [silentUplink, setSilentUplink] = useState(false);
  const [connectionQuality, setConnectionQuality] = useState<ConnectionQuality>(IDLE_CONNECTION_QUALITY);
  // Consecutive failed runtime starts; null once the phone registers. Drives
  // the agent-facing "check your connection" hint, never the retry loop.
  const transportDropsRef = useRef(0);
  const callLinkCountersRef = useRef<CallLinkCounters | null>(null);
  // The owner tab's registration verdict as mirrored over the bus; null until a
  // snapshot carrying one arrives (an owner on the previous build sends none).
  const mirroredRegistrationRef = useRef<{ down: boolean; retrying: boolean } | null>(null);
  // This tab's own graded verdict, kept separately from connectionQuality
  // because it is the one part of the grade that must stay current while the
  // tab is hidden: it is what mirrors render from.
  const [registrationFault, setRegistrationFault] = useState({ down: false, retrying: false });
  const [incomingCall, setIncomingCall] = useState<SoftphoneIncomingCall | null>(null);
  const [state, setState] = useState<SoftphoneState>({
    phase: enabledForRole ? 'connecting' : 'disabled',
    extension: null,
    message: enabledForRole ? 'Starting browser phone...' : 'Softphone not required for this role.',
    remoteIdentity: null,
  });
  // Sticky device fault (no microphone / permission revoked). Kept OUTSIDE the
  // phase machine on purpose: phases are transient (a failed session's teardown
  // flips the phone back to 'online' because it is still SIP-registered), which
  // is exactly how a missing headset burned a lead every ~1.3s on Jul 15 — the
  // dialer saw 'online' again after each failed auto-answer and claimed the
  // next contact. Cleared only when the device provably comes back (see the
  // devicechange recovery effect) or a call actually acquires audio.
  const [deviceError, setDeviceErrorState] = useState<string | null>(null);
  const deviceErrorRef = useRef<string | null>(null);
  // Backstop for auto-answer failures classifyMediaError can't attribute to a
  // device: enough consecutive ones trip the same sticky breaker (see
  // ANSWER_LOOP_TRIP_THRESHOLD / GENERIC_FAULT_COOLDOWN_MS).
  const consecutiveAutoAnswerFailsRef = useRef(0);
  const genericFaultTrippedAtRef = useRef<number | null>(null);
  const applyDeviceError = useCallback((message: string | null) => {
    deviceErrorRef.current = message;
    setDeviceErrorState(message);
  }, []);
  // What consumers see: while a device fault is known, the phone never
  // presents as 'online' — softphoneReadyForOutbound and every other
  // online-gated consumer stop without needing to know about device faults.
  const presentedState = useMemo<SoftphoneState>(() => (
    deviceError && state.phase === 'online'
      ? { ...state, phase: 'error', message: deviceError }
      : state
  ), [deviceError, state]);

  // CRM identity of the remote party: the caller for inbound calls, the dialed
  // number for outbound. (toPhone/destination on an inbound call hold the
  // agent's own DID — looking those up never matched the caller.)
  const remotePartyPhone = activeCall
    ? (activeCall.source === 'inbound'
      ? activeCall.fromPhone || state.remoteIdentity
      : activeCall.toPhone || activeCall.destination)
    : (state.remoteIdentity || dialDestination);
  const { contact: matchedContact, status: matchedContactStatus } = useContactByPhone(remotePartyPhone);

  const remoteAudioRef = useRef<HTMLAudioElement | null>(null);
  const reconnectTimerRef = useRef<number | null>(null);
  // Consecutive failed start attempts, for the retry backoff. Reset the moment
  // the phone registers, so the next outage starts fast again.
  const reconnectAttemptsRef = useRef(0);
  const phoneRegisteredRef = useRef(false);
  // When this tab's phone last went (or started out) without a binding, for the
  // connection bars. Stamped at the transition rather than on the bars' own
  // tick, which skips while the tab is hidden: an agent who spends ten minutes
  // in another tab while his phone is down must come back to red immediately,
  // not to a fresh grace window at the moment he goes to dial. null means there
  // is nothing to time — registered, not this tab's phone, or no runtime has
  // tried yet.
  const registrationDownSinceRef = useRef<number | null>(null);
  const runtimeRef = useRef<{
    userAgent: UserAgent | null;
    registerer: Registerer | null;
    session: Session | null;
    stopped: boolean;
    // True while this tab's runtime is a make-before-break lame duck (lease
    // lost, registration intentionally kept alive). Transport/registerer
    // listeners check this instead of the per-effect-run `cancelled` flag so
    // they come back to life if the lease is reclaimed and the runtime reused.
    muted: boolean;
  }>({
    userAgent: null,
    registerer: null,
    session: null,
    stopped: false,
    muted: false,
  });
  // This tab lost the lease but its runtime is still up: make-before-break
  // means it IS the phone (live registration, possibly a live call) until the
  // handoff completes. Three readers have to agree about that — the SIP effect
  // keeps the runtime alive instead of tearing it down, the bus refuses to
  // overwrite its UI with the winner's mirror, and the connection bars speak
  // for its own binding rather than reading a verdict about somebody else's.
  const isLameDuck = useCallback(() => (
    Boolean(runtimeRef.current.userAgent) && !runtimeRef.current.stopped
  ), []);
  // Builds a Registerer wired to the current runtime's state handling. Set by
  // the runtime effect (it closes over that run's credentials) and used by
  // reconnectInPlace to mint a replacement with a new Call-ID.
  const createRegistererRef = useRef<((userAgent: UserAgent) => Registerer) | null>(null);
  // X-Sales-Call-Id of the live SIP session. This is the authoritative "which
  // call am I actually on" — snapshot pickers prefer it over heuristics so the
  // UI can never render a different tracked call than the one carrying audio.
  const sessionSalesCallIdRef = useRef<string | null>(null);
  // A second inbound call ringing while the primary session slot is occupied.
  const pendingInviteRef = useRef<Invitation | null>(null);
  const adoptInvitationRef = useRef<
    ((invitation: Invitation, options: { salesCallId: string | null; isInternalDialerLeg: boolean }) => void) | null
  >(null);
  const toneRef = useRef<{
    context: AudioContext | null;
    gain: GainNode | null;
    cycleTimer: number | null;
    active: boolean;
    kind: SoftphoneToneKind | null;
  }>({
    context: null,
    gain: null,
    cycleTimer: null,
    active: false,
    kind: null,
  });
  const identitySyncRequestRef = useRef(0);
  const latestStateRef = useRef<Pick<SoftphoneState, 'phase' | 'extension'>>({
    phase: state.phase,
    extension: state.extension,
  });
  const activeCallRef = useRef<CallSummary | null>(null);
  const recentCallsRef = useRef<CallSummary[]>([]);
  // Mirror of localAudioMuted for callbacks that must stay referentially stable:
  // depending on the state value would recreate attachPeerConnectionTelemetry,
  // which restarts the SIP runtime effect and tears down the live call.
  const localAudioMutedRef = useRef(false);
  const hasRuntimeLeaseRef = useRef(false);
  // The single writer for registration and the clock the bars time it against.
  // Registration is otherwise flipped from five places (teardown, runtime start,
  // the registerer's own listener, the start failure path) and every one of them
  // has to move the clock with it. Only a tab that IS the phone keeps a clock —
  // see registrationDownSince for why that has to clear rather than skip.
  const markPhoneRegistered = useCallback((registered: boolean) => {
    phoneRegisteredRef.current = registered;
    registrationDownSinceRef.current = registrationDownSince({
      previous: registrationDownSinceRef.current,
      isPhone: hasRuntimeLeaseRef.current || isLameDuck(),
      registered,
      nowMs: Date.now(),
    });
    if (registered) {
      // A registered phone ends the outage: the next failure starts the retry
      // schedule from its fast floor rather than wherever the last one left off.
      reconnectAttemptsRef.current = 0;
      // Coming back up needs no grace window, so clear the published verdict on
      // the edge instead of waiting for a tick — which, on a hidden owner tab,
      // the browser may have throttled to once a minute. The down edge stays on
      // the tick, where the grace window is applied.
      setRegistrationFault((current) => (current.down || current.retrying ? { down: false, retrying: false } : current));
    }
  }, [isLameDuck]);
  // Guards so overlapping transport-disconnect / ICE events don't stack attempts.
  const reconnectInFlightRef = useRef(false);
  const iceGraceTimerRef = useRef<number | null>(null);
  const iceRestartInFlightRef = useRef(false);
  const lastIceRestartAtRef = useRef(0);
  // ICE restart wanted while the SIP transport was down — replayed once the
  // in-place reconnect lands (a re-INVITE launched into a dead WebSocket can
  // never complete, and FreeSWITCH hangs up the call when its transaction
  // times out, killing media that was still flowing).
  const pendingIceRestartReasonRef = useRef<string | null>(null);
  // Stuck-media recovery. The watch follows one peer connection; the episode
  // spans the replacement lines its rebuilds bring, and resets on the first
  // one that connects. While a rebuild has the phone un-REGISTERed, the
  // registerer's own recovery and the pool's re-REGISTER nudges stand down.
  const mediaWatchRef = useRef<{
    peerConnection: RTCPeerConnection | null;
    callId: string | null;
    sinceMs: number | null;
    timer: number | null;
    fired: boolean;
  }>({ peerConnection: null, callId: null, sinceMs: null, timer: null, fired: false });
  const mediaEpisodeRef = useRef<MediaRebuildEpisode>(IDLE_MEDIA_REBUILD_EPISODE);
  const mediaRebuildTimerRef = useRef<number | null>(null);
  const mediaRebuildPendingRef = useRef<{ attempt: number; timer: number | null } | null>(null);
  const mediaRebuildInFlightRef = useRef(false);
  const rebuildStuckLineRef = useRef<(
    attempt: number,
    peerConnection: RTCPeerConnection,
    callId: string | null,
    deferredSinceMs: number | null,
  ) => void>(() => undefined);
  // users.softphone_media_rebuild, from the latest credentials: off, a stuck
  // line is only reported.
  const mediaRebuildEnabledRef = useRef(false);
  // Silent-uplink watch over the live peer connection; reset with it.
  const uplinkWatchRef = useRef<SilentUplinkWatch>(IDLE_SILENT_UPLINK_WATCH);
  const tabIdRef = useRef<string>(getSoftphoneTabId());
  // Last lease write, for the SSE-driven renewal's write-gap floor.
  const lastLeaseWriteAtRef = useRef(0);
  // Cross-tab bus (see softphone-tab-sync.ts): the owner tab publishes phone
  // snapshots and executes commands; every other tab mirrors the snapshots and
  // forwards its phone actions instead of running a runtime of its own.
  const busRef = useRef<SoftphoneTabBus | null>(null);
  // Bus handlers live in refs so the subscription effect never re-subscribes
  // on render churn (same discipline as the other listener refs here).
  const publishBusSnapshotRef = useRef<(() => void) | null>(null);
  const applyBusSnapshotRef = useRef<((snapshot: SoftphoneTabSnapshot) => void) | null>(null);
  const handleBusCommandRef = useRef<((command: SoftphoneTabCommand) => void) | null>(null);
  // Publish guard for recentCalls: only emit a new array identity when the
  // content actually changed (same discipline as isSameCallSnapshot — this
  // file has a render-churn history, see PR #476).
  const recentCallsFingerprintRef = useRef('');
  // Stream liveness: bumped by every named event (snapshot/calls/ping). The
  // reconcile timers only fetch when this goes silent past
  // STREAM_STALE_AFTER_MS — a healthy stream means zero steady-state polling.
  const streamLastEventAtRef = useRef(0);
  const streamOpenRef = useRef(false);
  // The delta-protocol mirror for the CURRENT stream effect run (null when the
  // effect is torn down). A ref, not state — render-churn history (PR #476) —
  // and exposed here only so forced refreshes (refreshCallSnapshot) can
  // rebaseline it with the full snapshot they fetched.
  const callStreamMirrorRef = useRef<CallStreamMirror | null>(null);
  // Last known-good phone credentials; lets a rebuild go straight to the
  // WSS connect instead of refetching (see CREDENTIALS_CACHE_TTL_MS).
  const credentialsCacheRef = useRef<{ value: SoftphoneCredentialsResponse; fetchedAt: number } | null>(null);
  // Forced-relay self-management (Aug 5: expired relay credentials in immortal
  // tabs and relay-unreachable networks both produced dead-air calls that
  // needed manual flag surgery). Once relay ICE fails, this page falls back to
  // direct for the rest of its lifetime; a timer rebuilds the runtime before
  // the relay credentials can expire.
  const relayFallbackRef = useRef(false);
  const relayActiveRef = useRef(false);
  const relayCredentialTimerRef = useRef<number | null>(null);
  // Make-before-break handoff state (see LEASE_HANDOFF_MAX_MS).
  const leaseHandoffRef = useRef<{ timer: number | null; active: boolean }>({ timer: null, active: false });
  const telemetryRef = useRef<{
    sessionId: string | null;
    traceEnabled: boolean;
    browserSampleIntervalMs: number;
    statsTimer: number | null;
    peerConnection: RTCPeerConnection | null;
    activeCallId: string | null;
    connectionCleanup: (() => void) | null;
  }>({
    sessionId: null,
    traceEnabled: false,
    browserSampleIntervalMs: 5000,
    statsTimer: null,
    peerConnection: null,
    activeCallId: null,
    connectionCleanup: null,
  });
  // Batched telemetry event uplink (see TELEMETRY_EVENT_FLUSH_MS). Queue and
  // timers live in refs only — this file has a render-churn history (PR #476).
  const telemetryEventQueueRef = useRef<TelemetryEventQueue | null>(null);
  useEffect(() => {
    // Tracks the PRESENTED phase (device faults included) so action guards
    // like dialTo's online check refuse to start calls a missing mic would kill.
    latestStateRef.current = {
      phase: presentedState.phase,
      extension: presentedState.extension,
    };
  }, [presentedState.extension, presentedState.phase]);

  useEffect(() => {
    activeCallRef.current = activeCall;
  }, [activeCall]);

  useEffect(() => {
    hasRuntimeLeaseRef.current = hasRuntimeLease;
  }, [hasRuntimeLease]);

  const buildOwnerLease = useCallback((): SoftphoneOwnerLease => ({
    tabId: tabIdRef.current,
    updatedAt: Date.now(),
    phase: latestStateRef.current.phase,
  }), []);

  const syncRuntimeLease = useCallback((): boolean => {
    if (!enabledForRole) {
      setHasRuntimeLease(false);
      return false;
    }

    const current = readSoftphoneOwnerLease();
    if (canClaimSoftphoneLease(current, tabIdRef.current)) {
      const nextLease = buildOwnerLease();
      writeSoftphoneOwnerLease(nextLease);
      lastLeaseWriteAtRef.current = nextLease.updatedAt;
      const confirmed = readSoftphoneOwnerLease();
      const ownsLease = confirmed?.tabId === tabIdRef.current;
      setHasRuntimeLease(ownsLease);
      return ownsLease;
    }

    const ownsLease = current?.tabId === tabIdRef.current;
    setHasRuntimeLease(Boolean(ownsLease));
    return Boolean(ownsLease);
  }, [buildOwnerLease, enabledForRole]);

  // Timer-independent lease renewal: Chrome throttles a hidden tab's timers to
  // one wake-up per minute after ~5 minutes, which is longer than the lease
  // TTL — an owner renewing only on the interval would look dead and get its
  // registration seized by another tab. SSE message callbacks are exempt from
  // throttling and the server pings every 15s, so the stream handlers call
  // this on every event to keep a healthy hidden owner's lease fresh.
  const renewOwnerLeaseFromStream = useCallback(() => {
    if (!hasRuntimeLeaseRef.current) {
      return;
    }
    const now = Date.now();
    if (now - lastLeaseWriteAtRef.current < OWNER_LEASE_MIN_WRITE_GAP_MS) {
      return;
    }
    const lease = buildOwnerLease();
    writeSoftphoneOwnerLease(lease);
    lastLeaseWriteAtRef.current = lease.updatedAt;
  }, [buildOwnerLease]);

  const releaseRuntimeLease = useCallback(() => {
    clearSoftphoneOwnerLease(tabIdRef.current);
    setHasRuntimeLease(false);
  }, []);

  // The call an unload of this tab would drop mid-conversation. Mirror tabs
  // carry no call audio, so leaving one is always safe.
  const unloadWouldDropCallId = useCallback((): string | null => {
    if (!hasRuntimeLeaseRef.current && !isLameDuck()) {
      return null;
    }
    return liveConversationCall(activeCallRef.current, recentCallsRef.current)?.callId ?? null;
  }, [isLameDuck]);

  // The chunk-error reload backstop would drop the call too; mid-call it
  // defers to the update reload, which waits for the call to end. Runs after
  // the ref-sync effects above, so the refs it reads are current.
  useEffect(() => {
    setChunkReloadLiveCall(Boolean(unloadWouldDropCallId()));
  }, [activeCall, recentCalls, hasRuntimeLease, unloadWouldDropCallId]);
  useEffect(() => () => setChunkReloadLiveCall(false), []);

  // Non-owner tabs don't run a phone — they forward the action to the owner
  // tab and see the result come back on the mirrored snapshot (including any
  // actionError the owner sets).
  const postOwnerCommand = useCallback((command: SoftphoneTabCommand) => {
    busRef.current?.post({ kind: 'command', tabId: tabIdRef.current, command });
  }, []);

  useEffect(() => {
    if (!enabledForRole) {
      releaseRuntimeLease();
      return undefined;
    }

    const renewLease = () => {
      void syncRuntimeLease();
    };

    renewLease();

    const timer = window.setInterval(renewLease, SOFTPHONE_OWNER_RENEW_MS);
    const handleStorage = (event: StorageEvent) => {
      if (event.key && event.key !== SOFTPHONE_OWNER_STORAGE_KEY) {
        return;
      }
      renewLease();
    };
    const handleVisibilityOrFocus = () => {
      renewLease();
    };
    const handlePageHide = () => {
      releaseRuntimeLease();
    };
    // Reloading mid-call closes the phone's socket and drops the contact
    // (pull-to-refresh on a phone, or a reload to "fix" bad audio), so ask
    // first. The lease stays put while the agent decides: pagehide releases
    // it if the page really goes. The deploy auto-reload waits for no call up,
    // so it never prompts; the chunk-error backstop can, which beats a drop.
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (unloadWouldDropCallId()) {
        event.preventDefault();
        event.returnValue = '';
        return;
      }
      releaseRuntimeLease();
    };

    window.addEventListener('storage', handleStorage);
    window.addEventListener('focus', handleVisibilityOrFocus);
    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityOrFocus);

    return () => {
      window.clearInterval(timer);
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('focus', handleVisibilityOrFocus);
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityOrFocus);
      releaseRuntimeLease();
    };
  }, [enabledForRole, releaseRuntimeLease, syncRuntimeLease, unloadWouldDropCallId]);

  const clearReconnectTimer = useCallback(() => {
    if (reconnectTimerRef.current !== null) {
      window.clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  }, []);

  const scheduleReconnect = useCallback(() => {
    clearReconnectTimer();
    // Every retry re-fetches credentials and re-opens the WebSocket, so a flat
    // interval makes a failing link carry more traffic the longer it fails.
    // The first retries stay at the old flat interval — most failures are a
    // blip — and only the tail backs off, capped so the phone always comes
    // back on its own once the link does.
    //
    // The attempt counter is charged by startPhone, not here: one failed start
    // arms this from up to three places (the transport's onDisconnect, the
    // registerer going Unregistered, and the catch), because runtimeRef's
    // UserAgent is assigned before the two awaited SIP steps and so both
    // listeners are live while they fail. Counting arms instead of attempts
    // skipped the schedule ahead by 2-3 steps per cycle and pushed the third
    // retry past the pool's 90s seat hold.
    const delayMs = softphoneRetryDelayMs(reconnectAttemptsRef.current);
    reconnectTimerRef.current = window.setTimeout(() => {
      setRetryNonce((current) => current + 1);
    }, delayMs);
  }, [clearReconnectTimer]);

  // A backoff interval can still be running when the link comes back. The
  // browser saying it is online again is better evidence than the timer, so
  // collapse the remaining wait instead of making the agent sit it out. Only
  // ever touches a retry that is already pending — never a healthy phone.
  const restartPhoneNow = useCallback(() => {
    clearReconnectTimer();
    setRetryNonce((current) => current + 1);
  }, [clearReconnectTimer]);

  const resumeReconnectNow = useCallback(() => {
    if (reconnectTimerRef.current !== null) {
      restartPhoneNow();
    }
  }, [restartPhoneNow]);

  const resetAudioElement = useCallback(() => {
    const audioEl = remoteAudioRef.current;
    if (!audioEl) {
      return;
    }
    audioEl.pause();
    audioEl.srcObject = null;
  }, []);

  const stopTone = useCallback(() => {
    const runtime = toneRef.current;
    runtime.active = false;
    runtime.kind = null;
    if (runtime.cycleTimer !== null) {
      window.clearInterval(runtime.cycleTimer);
      runtime.cycleTimer = null;
    }
    if (runtime.gain) {
      runtime.gain.disconnect();
      runtime.gain = null;
    }
    const context = runtime.context;
    runtime.context = null;
    if (context) {
      void context.close().catch(() => undefined);
    }
  }, []);

  const startTone = useCallback(async (kind: SoftphoneToneKind) => {
    const runtime = toneRef.current;
    if (runtime.active && runtime.kind === kind) {
      return;
    }
    if (runtime.active) {
      stopTone();
    }

    const audioWindow = window as Window & {
      AudioContext?: typeof AudioContext;
      webkitAudioContext?: typeof AudioContext;
    };
    const AudioContextCtor = audioWindow.AudioContext ?? audioWindow.webkitAudioContext;
    if (!AudioContextCtor) {
      return;
    }
    runtime.context ??= new AudioContextCtor();

    const context = runtime.context;
    if (context.state === 'suspended') {
      await context.resume().catch(() => undefined);
    }

    const pattern = TONE_PATTERNS[kind];
    const gain = context.createGain();
    gain.gain.value = pattern.gain;
    gain.connect(context.destination);
    runtime.gain = gain;
    runtime.active = true;
    runtime.kind = kind;

    const playCycle = () => {
      const current = toneRef.current;
      if (!current.active || !current.context || !current.gain || current.kind !== kind) {
        return;
      }
      for (const burst of pattern.bursts) {
        const startAt = current.context.currentTime + (burst.atMs / 1000);
        const stopAt = startAt + (burst.durMs / 1000);
        const oscA = current.context.createOscillator();
        const oscB = current.context.createOscillator();
        oscA.type = 'sine';
        oscB.type = 'sine';
        oscA.frequency.setValueAtTime(TONE_FREQ_A, startAt);
        oscB.frequency.setValueAtTime(TONE_FREQ_B, startAt);
        oscA.connect(current.gain);
        oscB.connect(current.gain);
        oscA.start(startAt);
        oscB.start(startAt);
        oscA.stop(stopAt);
        oscB.stop(stopAt);
        oscA.onended = () => oscA.disconnect();
        oscB.onended = () => oscB.disconnect();
      }
    };

    playCycle();
    runtime.cycleTimer = window.setInterval(playCycle, pattern.cycleMs);
  }, [stopTone]);

  const stopTelemetryStats = useCallback(() => {
    const telemetry = telemetryRef.current;
    if (telemetry.statsTimer !== null) {
      window.clearInterval(telemetry.statsTimer);
      telemetry.statsTimer = null;
    }
    if (telemetry.connectionCleanup) {
      telemetry.connectionCleanup();
      telemetry.connectionCleanup = null;
    }
    telemetry.peerConnection = null;
    telemetry.activeCallId = null;
  }, []);

  const sendTelemetryEventBatch = useCallback(async (events: TelemetrySessionQueuedEvent[]): Promise<boolean> => {
    const sessionId = telemetryRef.current.sessionId;
    if (!sessionId) {
      return false;
    }
    await sipApi.recordTelemetrySessionEvents(sessionId, events);
    return true;
  }, []);

  const getTelemetryEventQueue = useCallback((): TelemetryEventQueue => {
    telemetryEventQueueRef.current ??= createTelemetryEventQueue(sendTelemetryEventBatch);
    return telemetryEventQueueRef.current;
  }, [sendTelemetryEventBatch]);

  const recordTelemetrySessionEvent = useCallback(async (
    eventName: string,
    severity: 'info' | 'warning' | 'error' = 'info',
    payload: Record<string, unknown> = {},
    callId?: string | null
  ) => {
    getTelemetryEventQueue().enqueue({
      eventName,
      severity,
      callId: callId || telemetryRef.current.activeCallId || null,
      payload,
      clientAt: new Date().toISOString(),
    });
  }, [getTelemetryEventQueue]);

  // Teardown send: a normal fetch would be cancelled with the page, so hand
  // the batch to the browser instead.
  const beaconTelemetryEvents = useCallback((events: TelemetrySessionQueuedEvent[]) => {
    const sessionId = telemetryRef.current.sessionId;
    if (!sessionId || !events.length) {
      return;
    }
    const url = `/api/sip/telemetry/session/${sessionId}/events`;
    const body = JSON.stringify({ events });
    let handedOff = false;
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      try {
        handedOff = navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
      } catch {
        handedOff = false;
      }
    }
    if (!handedOff) {
      void fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body,
        keepalive: true,
      }).catch(() => undefined);
    }
  }, []);

  // Tail flush for pagehide/session stop.
  const flushTelemetryEventsOnTeardown = useCallback(() => {
    const queue = telemetryEventQueueRef.current;
    if (queue && telemetryRef.current.sessionId) {
      beaconTelemetryEvents(queue.drainForFinalFlush());
    }
  }, [beaconTelemetryEvents]);

  useEffect(() => {
    if (!enabledForRole) {
      return undefined;
    }
    const timer = window.setInterval(() => {
      void getTelemetryEventQueue().flush();
    }, TELEMETRY_EVENT_FLUSH_MS);
    const handleBeforeUnload = () => {
      flushTelemetryEventsOnTeardown();
    };
    // pagehide, not beforeunload: the agent can still cancel the leave prompt.
    // Beaconed on its own, never enqueued: an enqueue can start a plain-fetch
    // flush the unload cancels, and a full queue drains past it.
    const handlePageHide = (event: PageTransitionEvent) => {
      const droppedCallId = unloadWouldDropCallId();
      if (droppedCallId) {
        beaconTelemetryEvents([{
          eventName: 'unload_during_call',
          severity: 'warning',
          callId: droppedCallId,
          payload: { persisted: event.persisted, visibility: document.visibilityState },
          clientAt: new Date().toISOString(),
        }]);
      }
      // The switch keeps this page's binding until the reloaded tab
      // REGISTERs (or an OPTIONS ping fails), so a pooled line parked on it
      // would still look alive; this report is what writes it off. A page
      // headed for the back/forward cache may come back on the same binding.
      const contact = runtimeRef.current.userAgent?.contact.uri.toString();
      if (contact && !event.persisted) {
        beaconTelemetryEvents([{
          eventName: 'softphone_unloaded',
          severity: 'info',
          callId: null,
          payload: { contact },
          clientAt: new Date().toISOString(),
        }]);
      }
      flushTelemetryEventsOnTeardown();
    };
    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      flushTelemetryEventsOnTeardown();
    };
  }, [beaconTelemetryEvents, enabledForRole, flushTelemetryEventsOnTeardown, getTelemetryEventQueue, unloadWouldDropCallId]);

  // Two-way device health probe, shared by the devicechange/permission
  // listeners and the while-faulted poll below.
  //
  // Healthy → faulted: total mic loss or a permission revoke trips the sticky
  // fault the moment the browser reports it — before a single dial is burned —
  // instead of waiting for the first failed auto-answer.
  //
  // Faulted → healthy: presence + permission + an actual getUserMedia open
  // must all pass before the fault clears and the runtime rebuilds. The
  // getUserMedia step is what catches the "another app released the mic" case
  // (no devicechange event exists for that); it is permission-gated so a
  // background probe can never pop the permission prompt.
  const probeDeviceHealth = useCallback(async () => {
    const present = await hasAudioInputDevice();
    let permission: PermissionState | null = null;
    try {
      const status = await navigator.permissions.query({ name: 'microphone' as PermissionName });
      permission = status.state;
    } catch {
      // Permissions API unavailable — treat as unknown.
    }

    if (!deviceErrorRef.current) {
      if (!present) {
        void recordTelemetrySessionEvent('microphone_missing', 'error', { at: 'devicechange' }, null);
        applyDeviceError(NO_MICROPHONE_MESSAGE);
        setActionError(NO_MICROPHONE_MESSAGE);
      } else if (permission === 'denied') {
        void recordTelemetrySessionEvent('microphone_denied', 'error', { at: 'permissionchange' }, null);
        applyDeviceError(MICROPHONE_DENIED_MESSAGE);
        setActionError(MICROPHONE_DENIED_MESSAGE);
      }
      return;
    }

    if (!present || permission === 'denied' || permission === 'prompt') {
      return;
    }
    // Generic (unknown-cause) trips hold for a cooldown so a persistent
    // non-device fault can't oscillate back into a fast dial loop.
    if (genericFaultTrippedAtRef.current !== null
      && Date.now() - genericFaultTrippedAtRef.current < GENERIC_FAULT_COOLDOWN_MS) {
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((track) => track.stop());
    } catch {
      return; // still unusable (e.g. held by another app) — stay faulted
    }
    void recordTelemetrySessionEvent('microphone_recovered', 'info', {}, null);
    genericFaultTrippedAtRef.current = null;
    consecutiveAutoAnswerFailsRef.current = 0;
    applyDeviceError(null);
    setActionError(null);
    scheduleReconnect();
  }, [applyDeviceError, recordTelemetrySessionEvent, scheduleReconnect]);

  useEffect(() => {
    if (!enabledForRole || typeof navigator === 'undefined') {
      return;
    }
    const media = optionalMediaDevices();
    const permissions = optionalPermissions();
    if (!media) {
      return;
    }
    let disposed = false;
    let permissionStatus: PermissionStatus | null = null;
    const onChange = () => {
      if (!disposed) {
        void probeDeviceHealth();
      }
    };
    media.addEventListener('devicechange', onChange);
    permissions?.query({ name: 'microphone' as PermissionName })
      .then((status) => {
        if (disposed) {
          return;
        }
        permissionStatus = status;
        status.addEventListener('change', onChange);
      })
      .catch(() => undefined);
    return () => {
      disposed = true;
      media.removeEventListener('devicechange', onChange);
      permissionStatus?.removeEventListener('change', onChange);
    };
  }, [enabledForRole, probeDeviceHealth]);

  // Poll while faulted: busy-mic release and missed devicechange events have
  // no push signal, so recovery needs a heartbeat. Runs only during a fault —
  // zero steady-state cost.
  useEffect(() => {
    if (!enabledForRole || !deviceError) {
      return;
    }
    const timer = window.setInterval(() => {
      void probeDeviceHealth();
    }, DEVICE_FAULT_REPROBE_MS);
    return () => window.clearInterval(timer);
  }, [deviceError, enabledForRole, probeDeviceHealth]);

  const readTransportState = useCallback((): string | null => {
    const transport = runtimeRef.current.userAgent?.transport as { isConnected?: () => boolean } | undefined;
    return transport?.isConnected?.() ? 'connected' : null;
  }, []);

  const flushPeerConnectionSample = useCallback(async () => {
    const telemetry = telemetryRef.current;
    if (!telemetry.sessionId || !telemetry.traceEnabled || !telemetry.peerConnection || !telemetry.activeCallId) {
      return;
    }
    const sample = await buildWebRtcSample(telemetry.peerConnection).catch(() => null);
    if (!sample) {
      return;
    }
    await sipApi.recordCallMetricSamples(telemetry.sessionId, telemetry.activeCallId, [sample]).catch(() => undefined);
  }, []);

  const applyLocalAudioMute = useCallback((muted: boolean): boolean => {
    const session = runtimeRef.current.session as (Session & {
      sessionDescriptionHandler?: { peerConnection?: RTCPeerConnection };
    }) | null;
    const peerConnection = session?.sessionDescriptionHandler?.peerConnection ?? telemetryRef.current.peerConnection;
    const audioSenders = peerConnection?.getSenders().filter((sender) => sender.track?.kind === 'audio') ?? [];
    audioSenders.forEach((sender) => {
      if (sender.track) {
        sender.track.enabled = !muted;
      }
    });
    return audioSenders.length > 0;
  }, []);

  const setLocalAudioMuted = useCallback((muted: boolean) => {
    if (!hasRuntimeLeaseRef.current) {
      postOwnerCommand({ type: 'set_muted', muted });
      return;
    }
    localAudioMutedRef.current = muted;
    setLocalAudioMutedState(muted);
    const applied = applyLocalAudioMute(muted);
    if (!applied && muted) {
      console.warn('[softphone] mute requested before local audio sender was available');
    }
  }, [applyLocalAudioMute, postOwnerCommand]);

  // Persist + live-apply the preferred microphone. On an active call the new
  // track replaces the sender's current one in place (no re-INVITE); otherwise
  // the preference applies via constraints on the next call. Throws so the
  // Audio modal can show why an explicit pick failed.
  const setAudioInputDevice = useCallback(async (deviceId: string | null): Promise<void> => {
    writePreferredInputDeviceId(deviceId);
    if (!hasRuntimeLeaseRef.current) {
      postOwnerCommand({ type: 'set_audio_input', deviceId });
      return;
    }
    const session = runtimeRef.current.session as (Session & {
      sessionDescriptionHandler?: { peerConnection?: RTCPeerConnection };
    }) | null;
    const peerConnection = session?.sessionDescriptionHandler?.peerConnection;
    const sender = peerConnection?.getSenders().find((s) => s.track?.kind === 'audio');
    if (!sender) {
      return;
    }
    // `exact`: an explicit pick that can't be opened must fail loudly here,
    // not silently fall back the way per-call `ideal` constraints do.
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: deviceId ? { deviceId: { exact: deviceId } } : true,
      video: false,
    });
    const track = stream.getAudioTracks().at(0);
    if (!track) {
      return;
    }
    const previous = sender.track;
    await sender.replaceTrack(track);
    previous?.stop();
    applyLocalAudioMute(localAudioMutedRef.current);
    // A successful switch proves the mic works — clear any stale device fault.
    if (deviceErrorRef.current) {
      genericFaultTrippedAtRef.current = null;
      consecutiveAutoAnswerFailsRef.current = 0;
      applyDeviceError(null);
    }
    void recordTelemetrySessionEvent('audio_input_switched', 'info', { midCall: true }, null);
  }, [applyDeviceError, applyLocalAudioMute, postOwnerCommand, recordTelemetrySessionEvent]);

  // Persist + live-apply the preferred speaker on the remote-audio element.
  const setAudioOutputDevice = useCallback(async (deviceId: string | null): Promise<void> => {
    writePreferredOutputDeviceId(deviceId);
    if (!hasRuntimeLeaseRef.current) {
      postOwnerCommand({ type: 'set_audio_output', deviceId });
      return;
    }
    await applyPreferredSink(remoteAudioRef.current);
  }, [postOwnerCommand]);

  const publishRecentCalls = useCallback((calls: CallSummary[]) => {
    const fingerprint = calls.map((call) => `${call.callId}:${call.status}:${call.updatedAt}`).join('|');
    if (fingerprint === recentCallsFingerprintRef.current) {
      return;
    }
    recentCallsFingerprintRef.current = fingerprint;
    recentCallsRef.current = calls;
    setRecentCalls(calls);
  }, []);

  const isStreamFresh = useCallback(
    () => streamOpenRef.current && Date.now() - streamLastEventAtRef.current < STREAM_STALE_AFTER_MS,
    []
  );

  // On-demand tracked-snapshot refresh. Default is stream-gated (a fresh
  // stream means the fetch is redundant — no-op); `force` punches through for
  // consumers hunting a terminal row the stream may have dropped (post-call
  // watchdogs) or for explicit user-initiated refreshes.
  const refreshCallSnapshot = useCallback(async (options?: { force?: boolean }): Promise<void> => {
    if (!hasRuntimeLeaseRef.current) {
      // The owner runs the SSE stream; it refreshes and the result arrives on
      // the mirrored snapshot, so every tab sees the same call feed.
      postOwnerCommand({ type: 'refresh_snapshot', force: Boolean(options?.force) });
      return;
    }
    if (!options?.force && isStreamFresh()) {
      return;
    }
    try {
      const payload = await fetchJson<{ ok: true; calls: CallSummary[] }>(ACTIVE_CALL_SNAPSHOT_PATH);
      // Rebaseline the delta mirror so the next stream delta applies on top of
      // this full snapshot rather than the mirror's older view.
      callStreamMirrorRef.current?.applyFull(payload.calls);
      publishRecentCalls(payload.calls);
    } catch {
      // Best-effort: the stream remains the primary feed.
    }
  }, [isStreamFresh, postOwnerCommand, publishRecentCalls]);

  // True while the active session is in a non-terminal state. SessionState enum
  // values are these exact strings (see promotePendingInvite below), compared
  // literally so this stays usable outside the sip.js runtime effect.
  const hasLiveSession = useCallback(() => {
    const session = runtimeRef.current.session as (Session & { state?: string }) | null;
    if (!session) {
      return false;
    }
    return session.state !== 'Terminated' && session.state !== 'Terminating';
  }, []);

  // NET-227: heal the media path on a network change by re-INVITing with an ICE
  // restart. The peer connection's audio (ICE/DTLS/SRTP, usually via TURN) is
  // independent of the SIP signaling socket, so a single re-INVITE can recover
  // media without renegotiating the whole call. Debounced + cooldown-gated so a
  // flapping link can't spam re-INVITEs.
  const triggerIceRestart = useCallback((reason: string) => {
    if (iceRestartInFlightRef.current) {
      return;
    }
    // The re-INVITE needs a live signaling path. Sent into a dead WebSocket it
    // can only fail — and worse, FreeSWITCH tears down the (media-healthy)
    // call when its side of the transaction times out. Park the intent; the
    // in-place reconnect replays it once the transport is back.
    if (readTransportState() !== 'connected' || reconnectInFlightRef.current) {
      pendingIceRestartReasonRef.current = reason;
      return;
    }
    const now = Date.now();
    if (now - lastIceRestartAtRef.current < ICE_RESTART_COOLDOWN_MS) {
      return;
    }
    const session = runtimeRef.current.session as (Session & {
      invite?: (options?: {
        sessionDescriptionHandlerOptions?: { offerOptions?: { iceRestart?: boolean } };
        requestDelegate?: { onAccept?: () => void; onReject?: () => void };
      }) => Promise<unknown>;
    }) | null;
    if (!session || typeof session.invite !== 'function' || !hasLiveSession()) {
      return;
    }
    iceRestartInFlightRef.current = true;
    lastIceRestartAtRef.current = now;
    void recordTelemetrySessionEvent('ice_restart_attempted', 'warning', {
      reason,
    }, telemetryRef.current.activeCallId);
    session.invite({
      sessionDescriptionHandlerOptions: { offerOptions: { iceRestart: true } },
      requestDelegate: {
        onAccept: () => {
          iceRestartInFlightRef.current = false;
          // The re-INVITE swaps in a fresh local audio sender; re-apply mute state.
          applyLocalAudioMute(localAudioMutedRef.current);
        },
        onReject: () => {
          iceRestartInFlightRef.current = false;
        },
      },
    }).catch(() => {
      iceRestartInFlightRef.current = false;
    });
  }, [applyLocalAudioMute, hasLiveSession, readTransportState, recordTelemetrySessionEvent]);

  // The live session's own call, as the call stream sees it — null until the
  // stream has delivered it. Only a pool line has a seat-safe rebuild.
  const readSessionCall = useCallback((callId: string | null): CallSummary | null => {
    if (!callId) {
      return null;
    }
    const active = activeCallRef.current;
    if (active?.callId === callId) {
      return active;
    }
    return recentCallsRef.current.find((call) => call.callId === callId) ?? null;
  }, []);

  const settleMediaRebuild = useCallback((outcome: 'succeeded' | 'failed', callId: string | null, reason?: string) => {
    const pending = mediaRebuildPendingRef.current;
    if (!pending) {
      return;
    }
    mediaRebuildPendingRef.current = null;
    if (pending.timer !== null) {
      window.clearTimeout(pending.timer);
    }
    void recordTelemetrySessionEvent(
      outcome === 'succeeded' ? 'media_rebuild_succeeded' : 'media_rebuild_failed',
      outcome === 'succeeded' ? 'info' : 'warning',
      { attempt: pending.attempt, ...(reason ? { reason } : {}) },
      callId,
    );
  }, [recordTelemetrySessionEvent]);

  // Ends the episode: a line that connected, a stuck line gone some other way,
  // a runtime restart. A later stuck line starts over with a full budget and
  // no banner.
  const resetMediaEpisode = useCallback(() => {
    if (mediaRebuildTimerRef.current !== null) {
      window.clearTimeout(mediaRebuildTimerRef.current);
      mediaRebuildTimerRef.current = null;
    }
    const pending = mediaRebuildPendingRef.current;
    if (pending?.timer != null) {
      window.clearTimeout(pending.timer);
    }
    mediaRebuildPendingRef.current = null;
    mediaEpisodeRef.current = IDLE_MEDIA_REBUILD_EPISODE;
    setMediaFailed(false);
  }, []);

  // Spends the next attempt of the episode on this line, or gives up.
  const planMediaRebuild = useCallback((peerConnection: RTCPeerConnection, callId: string | null) => {
    const { episode, decision } = nextMediaRebuild(mediaEpisodeRef.current, {
      poolLine: isPooledAgentLine(readSessionCall(callId)),
      enabled: mediaRebuildEnabledRef.current,
    });
    mediaEpisodeRef.current = episode;
    if (decision.action === 'give_up') {
      setMediaFailed(true);
    } else if (decision.action === 'rebuild') {
      if (mediaRebuildTimerRef.current !== null) {
        window.clearTimeout(mediaRebuildTimerRef.current);
      }
      mediaRebuildTimerRef.current = window.setTimeout(() => {
        mediaRebuildTimerRef.current = null;
        rebuildStuckLineRef.current(decision.attempt, peerConnection, callId, null);
      }, decision.delayMs);
    }
    return decision;
  }, [readSessionCall]);

  // The line as the stuck-media events report it.
  const describeMediaFlow = useCallback((
    peerConnection: RTCPeerConnection,
    sessionCall: CallSummary,
    flow: MediaFlowProbe,
  ) => {
    const poolLine = isPooledAgentLine(sessionCall);
    return {
      connectionState: peerConnection.connectionState,
      iceConnectionState: peerConnection.iceConnectionState,
      dtlsState: readDtlsState(peerConnection),
      prospectBridged: poolLine && liveConversationCall(sessionCall, recentCallsRef.current) !== null,
      poolLine,
      inboundPacketsBefore: flow.packetsBefore,
      inboundPacketsAfter: flow.packetsAfter,
    };
  }, []);

  // The stuck verdict, checked against the media before it counts: a line
  // still receiving RTP is reported as a suppressed false positive and left
  // alone; one that connected while the probe ran is reported as healed. Either
  // ends the episode the way a connected line does. The watch stays fired
  // either way: a line suppressed as flowing that never reaches a clear
  // reading is not judged again on this peer connection.
  const confirmStuckLine = useCallback(async (
    watch: typeof mediaWatchRef.current,
    peerConnection: RTCPeerConnection,
    callId: string | null,
    stuckMs: number,
  ) => {
    const flow = await probeMediaFlow(peerConnection);
    const sessionCall = readSessionCall(callId);
    if (mediaWatchRef.current !== watch || !sessionCall) {
      return;
    }
    const detail = { ...describeMediaFlow(peerConnection, sessionCall, flow), stuckMs };
    if (flow.verdict !== 'stalled') {
      settleMediaRebuild('succeeded', callId, flow.verdict);
      resetMediaEpisode();
      void recordTelemetrySessionEvent('media_stuck_suppressed', 'warning', { ...detail, stage: 'detect', reason: flow.verdict }, callId);
      return;
    }
    // A replacement line stuck again is the verdict on the attempt that brought it.
    settleMediaRebuild('failed', callId, 'stuck_again');
    const decision = planMediaRebuild(peerConnection, callId);
    void recordTelemetrySessionEvent('media_stuck_detected', 'error', {
      ...detail,
      action: decision.action,
      ...(decision.action === 'rebuild' ? { attempt: decision.attempt } : {}),
      ...(decision.action === 'skip' ? { reason: decision.reason } : {}),
    }, callId);
  }, [describeMediaFlow, planMediaRebuild, readSessionCall, recordTelemetrySessionEvent, resetMediaEpisode, settleMediaRebuild]);

  const watchMediaPath = useCallback((peerConnection: RTCPeerConnection, callId: string | null) => {
    const watch = mediaWatchRef.current;
    if (watch.peerConnection !== peerConnection) {
      if (watch.timer !== null) {
        window.clearTimeout(watch.timer);
      }
      mediaWatchRef.current = { peerConnection, callId, sinceMs: null, timer: null, fired: false };
      watchMediaPath(peerConnection, callId);
      return;
    }
    if (peerConnection.connectionState === 'connected') {
      settleMediaRebuild('succeeded', callId);
      resetMediaEpisode();
    }
    const verdict = assessMediaPath({
      connectionState: peerConnection.connectionState,
      iceConnectionState: peerConnection.iceConnectionState,
      dtlsState: readDtlsState(peerConnection),
      stuckSinceMs: watch.sinceMs,
      nowMs: Date.now(),
    });
    if (verdict.kind === 'watching') {
      watch.sinceMs = verdict.sinceMs;
      watch.timer ??= window.setTimeout(() => {
        watch.timer = null;
        if (mediaWatchRef.current === watch) {
          watchMediaPath(peerConnection, callId);
        }
      }, verdict.fireInMs);
      return;
    }
    if (watch.timer !== null) {
      window.clearTimeout(watch.timer);
      watch.timer = null;
    }
    if (verdict.kind === 'clear') {
      watch.sinceMs = null;
      watch.fired = false;
      return;
    }
    const sessionCall = readSessionCall(callId);
    // Whether this is a pool line is not known until the call stream has the
    // call; the next stream update re-assesses (see publishRecentCalls).
    if (watch.fired || !sessionCall) {
      return;
    }
    watch.fired = true;
    void confirmStuckLine(watch, peerConnection, callId, verdict.stuckMs);
  }, [confirmStuckLine, readSessionCall, resetMediaEpisode, settleMediaRebuild]);

  useEffect(() => {
    const watch = mediaWatchRef.current;
    if (watch.peerConnection && !watch.fired) {
      watchMediaPath(watch.peerConnection, watch.callId);
    }
  }, [activeCall, recentCalls, watchMediaPath]);

  // One silent-uplink tick. getStats is only read while the uplink can be
  // judged; a parked pool line or a muted agent costs nothing.
  const checkUplink = useCallback(async (peerConnection: RTCPeerConnection, callId: string | null) => {
    const conversation = liveConversationCall(readSessionCall(callId), recentCallsRef.current);
    const transceiver = audioTransceiver(peerConnection);
    let blockedBy: SilentUplinkBlock | null = null;
    if (localAudioMutedRef.current) {
      blockedBy = 'muted';
    } else if (conversation?.status !== 'bridged') {
      blockedBy = 'not_bridged';
    } else if (uplinkOnHold(transceiver?.currentDirection)) {
      blockedBy = 'on_hold';
    }
    const counters = blockedBy === null && transceiver ? await readUplinkCounters(transceiver) : null;
    if (telemetryRef.current.peerConnection !== peerConnection) {
      return;
    }
    const { watch, event } = assessUplink(uplinkWatchRef.current, {
      nowMs: Date.now(),
      blockedBy,
      packetsSent: counters?.packetsSent ?? null,
      packetsReceived: counters?.packetsReceived ?? null,
    });
    uplinkWatchRef.current = watch;
    if (!event) {
      return;
    }
    if (event.type === 'cleared') {
      setSilentUplink(false);
      void recordTelemetrySessionEvent('silent_uplink_cleared', 'info', { silentMs: event.silentMs, reason: event.reason }, callId);
      return;
    }
    setSilentUplink(true);
    const micTrack = transceiver?.sender.track ?? null;
    void recordTelemetrySessionEvent('silent_uplink_detected', 'error', {
      silentMs: event.silentMs,
      packetsSent: event.packetsSent,
      packetsReceived: event.packetsReceived,
      poolLine: isPooledAgentLine(readSessionCall(callId)),
      conversationCallId: conversation?.callId ?? null,
      connectionState: peerConnection.connectionState,
      direction: transceiver?.currentDirection ?? null,
      micTrack: micTrack ? { readyState: micTrack.readyState, muted: micTrack.muted, enabled: micTrack.enabled } : null,
    }, callId);
  }, [readSessionCall, recordTelemetrySessionEvent]);

  // The line is gone: records the end of a silence still flagged, once.
  const endSilentUplink = useCallback((callId: string | null) => {
    const event = endUplinkWatch(uplinkWatchRef.current, Date.now());
    uplinkWatchRef.current = IDLE_SILENT_UPLINK_WATCH;
    setSilentUplink(false);
    if (event?.type === 'cleared') {
      void recordTelemetrySessionEvent('silent_uplink_cleared', 'info', { silentMs: event.silentMs, reason: event.reason }, callId);
    }
  }, [recordTelemetrySessionEvent]);

  const attachPeerConnectionTelemetry = useCallback((callId: string | null, peerConnection: RTCPeerConnection | null) => {
    stopTelemetryStats();
    if (!callId || !peerConnection) {
      return;
    }
    telemetryRef.current.peerConnection = peerConnection;
    telemetryRef.current.activeCallId = callId;
    applyLocalAudioMute(localAudioMutedRef.current);

    const handleConnectionStateChange = () => {
      void recordTelemetrySessionEvent('peer_connection_state', 'info', {
        iceConnectionState: peerConnection.iceConnectionState,
        connectionState: peerConnection.connectionState,
        signalingState: peerConnection.signalingState,
      }, callId);

      watchMediaPath(peerConnection, callId);

      // A forced-relay connection whose ICE fails cannot be saved by an ICE
      // restart when the network can't reach the relay at all: fall back to
      // direct for the rest of this page lifetime and rebuild, so the agent
      // keeps dialing instead of dead-airing until someone edits their flag.
      if (relayActiveRef.current && !relayFallbackRef.current && peerConnection.iceConnectionState === 'failed') {
        relayFallbackRef.current = true;
        void recordTelemetrySessionEvent('relay_fallback_triggered', 'error', {
          iceConnectionState: peerConnection.iceConnectionState,
          connectionState: peerConnection.connectionState,
        }, callId);
        scheduleReconnect();
        return;
      }

      // NET-227: recover the media path on ICE trouble.
      const iceState = peerConnection.iceConnectionState;
      if (iceState === 'failed') {
        if (iceGraceTimerRef.current !== null) {
          window.clearTimeout(iceGraceTimerRef.current);
          iceGraceTimerRef.current = null;
        }
        triggerIceRestart('ice_failed');
      } else if (iceState === 'disconnected') {
        // ICE may self-heal; give it a short grace window before forcing a restart.
        iceGraceTimerRef.current ??= window.setTimeout(() => {
          iceGraceTimerRef.current = null;
          if (peerConnection.iceConnectionState === 'disconnected') {
            triggerIceRestart('ice_disconnect_grace');
          }
        }, ICE_DISCONNECT_GRACE_MS);
      } else if (iceState === 'connected' || iceState === 'completed') {
        pendingIceRestartReasonRef.current = null;
        if (iceGraceTimerRef.current !== null) {
          window.clearTimeout(iceGraceTimerRef.current);
          iceGraceTimerRef.current = null;
        }
      }
    };

    peerConnection.addEventListener('connectionstatechange', handleConnectionStateChange);
    peerConnection.addEventListener('iceconnectionstatechange', handleConnectionStateChange);

    // Established can land after the connection already settled, with no
    // further state change coming to start the watch.
    watchMediaPath(peerConnection, callId);

    // Silent uplink: its own light tick, traced or not, one read at a time.
    uplinkWatchRef.current = IDLE_SILENT_UPLINK_WATCH;
    let uplinkReadInFlight = false;
    const uplinkTimer = window.setInterval(() => {
      if (uplinkReadInFlight) {
        return;
      }
      uplinkReadInFlight = true;
      void checkUplink(peerConnection, callId).finally(() => {
        uplinkReadInFlight = false;
      });
    }, SILENT_UPLINK_TICK_MS);

    telemetryRef.current.connectionCleanup = () => {
      window.clearInterval(uplinkTimer);
      endSilentUplink(callId);
      peerConnection.removeEventListener('connectionstatechange', handleConnectionStateChange);
      peerConnection.removeEventListener('iceconnectionstatechange', handleConnectionStateChange);
      const watch = mediaWatchRef.current;
      if (watch.peerConnection === peerConnection) {
        if (watch.timer !== null) {
          window.clearTimeout(watch.timer);
        }
        mediaWatchRef.current = { peerConnection: null, callId: null, sinceMs: null, timer: null, fired: false };
      }
      if (iceGraceTimerRef.current !== null) {
        window.clearTimeout(iceGraceTimerRef.current);
        iceGraceTimerRef.current = null;
      }
      iceRestartInFlightRef.current = false;
    };


    if (!telemetryRef.current.traceEnabled) {
      return;
    }

    void flushPeerConnectionSample();
    telemetryRef.current.statsTimer = window.setInterval(() => {
      void flushPeerConnectionSample();
    }, telemetryRef.current.browserSampleIntervalMs);
  }, [applyLocalAudioMute, checkUplink, endSilentUplink, flushPeerConnectionSample, recordTelemetrySessionEvent, scheduleReconnect, stopTelemetryStats, triggerIceRestart, watchMediaPath]);

  // A session groups one phone runtime's events and call samples. The id is
  // minted here, synchronously, so no server round trip can race a restart.
  const beginTelemetrySession = useCallback((extension: string | null, trace: TelephonyTraceConfig) => {
    const telemetry = telemetryRef.current;
    telemetry.traceEnabled = trace.enabled;
    telemetry.browserSampleIntervalMs = trace.browserSampleIntervalMs;
    if (telemetry.sessionId) {
      return;
    }
    telemetry.sessionId = crypto.randomUUID();
    void recordTelemetrySessionEvent('session_started', 'info', {
      extension,
      userAgent: navigator.userAgent,
      visibilityState: document.visibilityState,
      network: readNetworkSnapshot(),
    });
  }, [recordTelemetrySessionEvent]);

  // ---- Make-before-break lease handoff --------------------------------------
  // When another tab takes the runtime lease, this tab's runtime (and the live
  // FS registration it holds) stays up until the winner reports online or the
  // grace cap fires. See LEASE_HANDOFF_MAX_MS.
  const cancelLeaseHandoff = useCallback(() => {
    const handoff = leaseHandoffRef.current;
    handoff.active = false;
    if (handoff.timer !== null) {
      window.clearTimeout(handoff.timer);
      handoff.timer = null;
    }
  }, []);

  const stopRuntime = useCallback(async (options?: { endTelemetry?: boolean; preserveSession?: boolean; skipUnregister?: boolean }) => {
    if (options?.preserveSession) {
      // NET-227 defensive guard: the SIP effect re-ran while an in-place reconnect
      // is healing a live call. Leave the UserAgent/session/registerer intact so
      // the call survives — only reconnectInPlace's own fallback tears down here.
      return;
    }
    // A pending deferred (lame-duck) stop must never outlive the runtime it
    // was scheduled for and kill its successor.
    cancelLeaseHandoff();
    runtimeRef.current.stopped = true;
    resetMediaEpisode();
    // Must finish before the first await below: callers fire this without
    // awaiting and start the next runtime immediately, which must begin a
    // session of its own rather than adopt this one.
    if (options?.endTelemetry && telemetryRef.current.sessionId) {
      // Ahead of the flush below: stopTelemetryStats runs after the session
      // id is forgotten.
      endSilentUplink(telemetryRef.current.activeCallId);
      void recordTelemetrySessionEvent('session_stopped', 'info', {
        reason: 'softphone_runtime_stopped',
        // Names the binding that dies with this UserAgent: the pool writes
        // off a parked line answered on it (see softphone_unloaded).
        contact: runtimeRef.current.userAgent?.contact.uri.toString() ?? null,
      });
      // Flush the tail now, before the session id is forgotten.
      flushTelemetryEventsOnTeardown();
      telemetryRef.current.sessionId = null;
    }
    markPhoneRegistered(false);
    clearReconnectTimer();
    if (relayCredentialTimerRef.current !== null) {
      window.clearTimeout(relayCredentialTimerRef.current);
      relayCredentialTimerRef.current = null;
    }
    stopTone();
    stopTelemetryStats();

    const pendingInvite = pendingInviteRef.current as (Invitation & { dispose?: () => Promise<void> | void }) | null;
    pendingInviteRef.current = null;
    setIncomingCall(null);
    if (pendingInvite?.dispose) {
      await Promise.resolve(pendingInvite.dispose()).catch(() => undefined);
    }

    const activeSession = runtimeRef.current.session as (Session & { dispose?: () => Promise<void> | void }) | null;
    runtimeRef.current.session = null;
    setPoolLineLive(false);
    sessionSalesCallIdRef.current = null;
    if (activeSession?.dispose) {
      await Promise.resolve(activeSession.dispose()).catch(() => undefined);
    }

    // Disposing the session here fires its own end listener, but that listener bails
    // when it sees runtimeRef.current.session is already null — so the call UI never
    // resets. Without this, a teardown mid-ring (e.g. a reconnect on a flaky network)
    // leaves the "Answer"/in-call card on screen pointing at a call that no longer
    // exists, and every click becomes a silent no-op. Clear the stale call UI here.
    if (activeSession) {
      activeCallRef.current = null;
      setActiveCall(null);
      setState((current) => (
        current.phase === 'ringing'
        || current.phase === 'calling'
        || current.phase === 'in_call'
        || current.phase === 'hot_transfer_pending'
          ? { ...current, phase: 'online', message: 'Call ended', remoteIdentity: null }
          : current
      ));
    }

    const registerer = runtimeRef.current.registerer as (Registerer & { unregister?: () => Promise<void> }) | null;
    runtimeRef.current.registerer = null;
    // skipUnregister: after a make-before-break handoff the winner's REGISTER
    // has already replaced this binding — an un-REGISTER here would tear down
    // (or race) the winner's registration. Closing the socket is enough.
    if (registerer?.unregister && !options?.skipUnregister) {
      await registerer.unregister().catch(() => undefined);
    }

    const userAgent = runtimeRef.current.userAgent;
    runtimeRef.current.userAgent = null;
    if (userAgent) {
      await userAgent.stop().catch(() => undefined);
    }

    resetAudioElement();
  }, [cancelLeaseHandoff, clearReconnectTimer, endSilentUplink, flushTelemetryEventsOnTeardown, markPhoneRegistered, recordTelemetrySessionEvent, resetAudioElement, resetMediaEpisode, stopTone, stopTelemetryStats]);

  const finishLeaseHandoff = useCallback((reason: 'winner_online' | 'grace_timeout') => {
    if (!leaseHandoffRef.current.active) {
      return;
    }
    cancelLeaseHandoff();
    void recordTelemetrySessionEvent('lease_handoff_finished', 'info', { reason });
    // Nudge the new owner to re-REGISTER once our socket is gone, in case our
    // periodic refresh re-took the binding mid-handoff (see SOFTPHONE_REASSERT_KEY).
    try {
      window.localStorage.setItem(SOFTPHONE_REASSERT_KEY, String(Date.now()));
    } catch {
      // best-effort
    }
    void stopRuntime({ endTelemetry: true, skipUnregister: true }).then(() => {
      stopTone();
      activeCallRef.current = null;
      setActiveCall(null);
      setActionError(null);
      setState({
        phase: 'idle',
        extension: null,
        message: 'Browser phone is active in another tab.',
        remoteIdentity: null,
      });
      // Now that the lame-duck runtime is gone, this tab is a plain mirror —
      // ask the owner for its current state (snapshots were skipped while the
      // local runtime was still the real phone).
      busRef.current?.post({ kind: 'hello', tabId: tabIdRef.current });
    });
  }, [cancelLeaseHandoff, recordTelemetrySessionEvent, stopRuntime, stopTone]);

  const beginLeaseHandoff = useCallback(() => {
    const handoff = leaseHandoffRef.current;
    if (handoff.active) {
      return;
    }
    handoff.active = true;
    // Mute (not stop): registration refreshes keep running so the binding
    // stays alive, but transport/registerer listeners must not fight the
    // winner with reconnects or state updates.
    runtimeRef.current.muted = true;
    void recordTelemetrySessionEvent('lease_handoff_started', 'info', {});
    handoff.timer = window.setTimeout(() => finishLeaseHandoff('grace_timeout'), LEASE_HANDOFF_MAX_MS);
  }, [finishLeaseHandoff, recordTelemetrySessionEvent]);

  // Make-before-break wiring: finish the handoff the moment the winner's
  // lease write reports 'online', and answer reassert nudges from a departing
  // lame duck with a fresh REGISTER (atomic replace, idempotent).
  useEffect(() => {
    if (!enabledForRole) {
      return undefined;
    }
    const handleStorage = (event: StorageEvent) => {
      if (event.key === SOFTPHONE_REASSERT_KEY) {
        const runtime = runtimeRef.current;
        if (hasRuntimeLeaseRef.current && phoneRegisteredRef.current && runtime.registerer && !runtime.stopped && !runtime.muted) {
          void runtime.registerer.register().then(() => undefined).catch(() => undefined);
        }
        return;
      }
      if (event.key !== SOFTPHONE_OWNER_STORAGE_KEY || !leaseHandoffRef.current.active) {
        return;
      }
      const owner = readSoftphoneOwnerLease();
      if (owner && owner.tabId !== tabIdRef.current && owner.phase === 'online') {
        finishLeaseHandoff('winner_online');
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [enabledForRole, finishLeaseHandoff]);

  // NET-227: recover a transport drop *without* tearing down the live call.
  // userAgent.reconnect() re-establishes the WebSocket in place, leaving the
  // session and its peer connection (and therefore the audio) untouched. Only
  // reached when a call is actually live; on exhausted attempts it falls back to today's full rebuild (scheduleReconnect).
  const reconnectInPlace = useCallback(async () => {
    if (reconnectInFlightRef.current) {
      return;
    }
    const userAgent = runtimeRef.current.userAgent;
    if (!userAgent) {
      scheduleReconnect();
      return;
    }
    reconnectInFlightRef.current = true;
    setReconnecting(true);
    const extension = latestStateRef.current.extension;
    setState((current) => ({
      ...current,
      phase: 'in_call',
      extension,
      message: 'Reconnecting — call still active…',
    }));
    void recordTelemetrySessionEvent('reconnect_in_place_started', 'info', {
    }, telemetryRef.current.activeCallId);

    let recovered = false;
    for (let attempt = 1; attempt <= RECONNECT_IN_PLACE_ATTEMPTS && !runtimeRef.current.stopped; attempt += 1) {
      // The runtime was rebuilt underneath this attempt: the new one owns the
      // registration now, so a swap here would retire its booting Registerer.
      if (runtimeRef.current.userAgent !== userAgent) {
        break;
      }
      try {
        await userAgent.reconnect();
        // Transport is back. Re-REGISTER unconditionally: the old binding rode
        // the DEAD connection and FreeSWITCH may have already pruned it
        // (unregister-on-options-fail) without this side ever hearing — the
        // Registerer still reports 'Registered'. REGISTER is an idempotent
        // replace, so repairing a binding that was actually fine costs one
        // message; trusting a stale one costs a reaped pool seat.
        //
        // On a FRESH Registerer, never the old one: re-registering on the old
        // instance reuses its Call-ID, and the OPTIONS ping FreeSWITCH sent on
        // the dead socket is keyed by that Call-ID — when it times out (~32 s
        // later) FreeSWITCH expires the binding it finds under it, i.e. the new
        // one, and the pool reaps the seat while this side still reads
        // Registered. The old instance's state changes are ignored by its
        // listener guard once the swap lands (see swapRegisterer).
        const createRegisterer = createRegistererRef.current;
        if (createRegisterer) {
          const callId = telemetryRef.current.activeCallId;
          const sentAt = Date.now();
          const registration = swapRegisterer(runtimeRef.current, createRegisterer(userAgent), {
            requestDelegate: {
              onAccept: () => {
                void recordTelemetrySessionEvent('reregister_after_reconnect', 'info', {
                  outcome: 'registered',
                  elapsedMs: Date.now() - sentAt,
                }, callId);
              },
              onReject: (response) => {
                void recordTelemetrySessionEvent('reregister_after_reconnect', 'warning', {
                  outcome: 'failed',
                  elapsedMs: Date.now() - sentAt,
                  message: `${response.message.statusCode ?? ''} ${response.message.reasonPhrase ?? ''}`.trim() || 'REGISTER rejected',
                }, callId);
              },
            },
          });
          // register() resolves once the request is handed to the transport.
          // Every final response — real rejections and the 408/503 sip.js
          // synthesizes for a timeout or transport error alike — reaches the
          // delegate above and the fresh Registerer's own stateChange
          // (Unregistered → existing recovery). This catch only sees a REGISTER
          // already in flight or a synchronous throw: a failed attempt here.
          await registration.catch((error: unknown) => {
            void recordTelemetrySessionEvent('reregister_after_reconnect', 'warning', {
              outcome: 'failed',
              elapsedMs: Date.now() - sentAt,
              message: error instanceof Error ? error.message : 'REGISTER not sent',
            }, callId);
            throw error;
          });
        }
        recovered = true;
        break;
      } catch (error) {
        void recordTelemetrySessionEvent('reconnect_in_place_failed', 'warning', {
          attempt,
          message: error instanceof Error ? error.message : 'reconnect failed',
        }, telemetryRef.current.activeCallId);
        await delay(RECONNECT_IN_PLACE_DELAY_MS * attempt);
      }
    }

    reconnectInFlightRef.current = false;
    setReconnecting(false);
    if (runtimeRef.current.stopped || runtimeRef.current.userAgent !== userAgent) {
      return;
    }
    if (recovered) {
      void recordTelemetrySessionEvent('transport_reconnected_in_place', 'info', {
      }, telemetryRef.current.activeCallId);
      if (hasLiveSession()) {
        // Keep phase on in_call so clearStaleCallUi doesn't wipe the still-live
        // call; the reconcile loop / SSE re-sync to the bridged FreeSWITCH leg.
        setState((current) => ({
          ...current,
          phase: 'in_call',
          extension,
          message: 'Phone online',
        }));
        // Replay an ICE restart that was parked while the transport was down.
        const pendingReason = pendingIceRestartReasonRef.current;
        pendingIceRestartReasonRef.current = null;
        if (pendingReason) {
          triggerIceRestart(pendingReason);
        }
      }
    } else {
      // Bounded attempts exhausted → fall back to today's full rebuild.
      void recordTelemetrySessionEvent('reconnect_in_place_failed', 'error', {
        exhausted: true,
      }, telemetryRef.current.activeCallId);
      scheduleReconnect();
    }
  }, [hasLiveSession, recordTelemetrySessionEvent, scheduleReconnect, triggerIceRestart]);

  // The seat as the server has it right now — the gate on the un-REGISTER
  // (see mediaRebuildSeatGate). Read fresh rather than from the pool
  // provider, whose view goes stale in a hidden tab and is empty while
  // loading; any doubt reads as null, which defers.
  const readFreshPoolSeat = useCallback(async (): Promise<MediaRebuildSeat | null> => {
    try {
      const { membership, restarting } = await withTimeout(
        pooledDialerApi.myMembership(),
        MEDIA_REBUILD_SEAT_READ_TIMEOUT_MS,
        'pool membership read timed out',
      );
      if (restarting || !membership) {
        return null;
      }
      const { member } = membership;
      return { state: member.state, inConversation: member.currentLegId !== null || member.inbound };
    } catch {
      return null;
    }
  }, []);

  // One attempt at rebuilding a stuck pool line (see runMediaRebuild), with
  // the episode's bookkeeping around it. A rebuild that cannot go yet
  // re-checks without spending the attempt, up to the deferral cap.
  const rebuildStuckLine = useCallback(async (
    attempt: number,
    peerConnection: RTCPeerConnection,
    callId: string | null,
    deferredSinceMs: number | null,
  ) => {
    const runtime = runtimeRef.current;
    const session = runtime.session as (Session & {
      sessionDescriptionHandler?: { peerConnection?: RTCPeerConnection };
    }) | null;
    const registerer = runtime.registerer;
    const stillStuck = () => {
      const current = runtimeRef.current.session as typeof session | null;
      return current?.sessionDescriptionHandler?.peerConnection === peerConnection
        && peerConnection.connectionState !== 'connected'
        && hasLiveSession();
    };
    // Replaced, ended or healed while the backoff ran: nothing left to rebuild.
    if (!session || !registerer || !stillStuck()) {
      return;
    }
    // The un-REGISTER takes the line down whatever is on it, so the media is
    // read once more right before it: a line that is receiving RTP works, and
    // rebuilding it would drop the prospect on it.
    const flow = await probeMediaFlow(peerConnection);
    if (!stillStuck()) {
      return;
    }
    if (flow.verdict !== 'stalled') {
      resetMediaEpisode();
      const sessionCall = readSessionCall(callId);
      void recordTelemetrySessionEvent('media_stuck_suppressed', 'warning', {
        ...(sessionCall ? describeMediaFlow(peerConnection, sessionCall, flow) : {}),
        stage: 'rebuild',
        reason: flow.verdict,
        attempt,
      }, callId);
      return;
    }
    const result = await runMediaRebuild({
      registerer,
      session,
      phoneReady: () => !runtimeRef.current.stopped
        && !runtimeRef.current.muted
        && runtimeRef.current.registerer === registerer
        && hasRuntimeLeaseRef.current
        && readTransportState() === 'connected'
        && !reconnectInFlightRef.current
        && !iceRestartInFlightRef.current
        && !mediaRebuildInFlightRef.current,
      readSeat: readFreshPoolSeat,
      canReRegister: () => runtimeRef.current.registerer === registerer
        && !runtimeRef.current.stopped
        && !runtimeRef.current.muted,
      onReRegisterFailed: () => {
        if (hasLiveSession()) {
          void reconnectInPlace();
        } else {
          scheduleReconnect();
        }
      },
      setInFlight: (inFlight) => {
        mediaRebuildInFlightRef.current = inFlight;
      },
      timeouts: {
        signalingMs: SOFTPHONE_REGISTER_TIMEOUT_MS,
        reapMs: MEDIA_REBUILD_REAP_WAIT_MS,
        bridgedReapMs: MEDIA_REBUILD_BRIDGED_REAP_WAIT_MS,
      },
    });
    if (result.outcome === 'released') {
      // The verdict lands with the replacement line: connected settles it as
      // a success, stuck again as a failure (see watchMediaPath).
      const superseded = mediaRebuildPendingRef.current;
      if (superseded?.timer != null) {
        window.clearTimeout(superseded.timer);
      }
      mediaRebuildPendingRef.current = {
        attempt,
        timer: window.setTimeout(() => {
          settleMediaRebuild('failed', callId, 'line_not_restored');
        }, mediaRebuildConfirmMs(result.prospectBridged)),
      };
      return;
    }
    if (!stillStuck()) {
      return;
    }
    if (result.outcome === 'deferred') {
      const sinceMs = deferredSinceMs ?? Date.now();
      if (!mediaRebuildDeferralExpired(sinceMs, Date.now())) {
        mediaRebuildTimerRef.current = window.setTimeout(() => {
          mediaRebuildTimerRef.current = null;
          rebuildStuckLineRef.current(attempt, peerConnection, callId, sinceMs);
        }, MEDIA_REBUILD_DEFER_RECHECK_MS);
        return;
      }
    }
    // The old line is still up: the next attempt, if any, goes at it again.
    const reason = result.outcome === 'deferred' ? `deferred_${result.reason}` : result.reason;
    void recordTelemetrySessionEvent('media_rebuild_failed', 'warning', { attempt, reason }, callId);
    planMediaRebuild(peerConnection, callId);
  }, [describeMediaFlow, hasLiveSession, planMediaRebuild, readFreshPoolSeat, readSessionCall, readTransportState, reconnectInPlace, recordTelemetrySessionEvent, resetMediaEpisode, scheduleReconnect, settleMediaRebuild]);

  useEffect(() => {
    rebuildStuckLineRef.current = (attempt, peerConnection, callId, deferredSinceMs) => {
      void rebuildStuckLine(attempt, peerConnection, callId, deferredSinceMs);
    };
  }, [rebuildStuckLine]);

  // Move a call-waiting invite into the primary session slot (ringing UI) once
  // the current call is gone. Returns false if there was nothing live to promote.
  const promotePendingInvite = useCallback((): boolean => {
    const pending = pendingInviteRef.current;
    if (!pending) {
      return false;
    }
    pendingInviteRef.current = null;
    setIncomingCall(null);
    const pendingState = String(pending.state);
    if (pendingState === 'Terminated' || pendingState === 'Terminating') {
      return false;
    }
    adoptInvitationRef.current?.(pending, {
      salesCallId: extractSalesCallHeader(pending),
      isInternalDialerLeg: false,
    });
    return true;
  }, []);

  // Take ownership of an invite as the softphone's single live session: wire up
  // state transitions, audio, telemetry, and (for internal dialer legs) auto-answer.
  const adoptInvitation = useCallback((
    invitation: Invitation,
    options: { salesCallId: string | null; isInternalDialerLeg: boolean },
  ) => {
    const { salesCallId, isInternalDialerLeg } = options;
    // Hot-transfer parked legs carry X-Hot-Transfer-Id. They still auto-answer as internal dialer legs,
    // but stay out of the normal "Dialing" call UI until the customer is actually bridged.
    const hotTransferId = extractHotTransferHeader(invitation);
    const isHotTransferLeg = isInternalDialerLeg && Boolean(hotTransferId);
    runtimeRef.current.session = invitation;
    sessionSalesCallIdRef.current = salesCallId;
    telemetryRef.current.activeCallId = salesCallId || telemetryRef.current.activeCallId || null;
    void recordTelemetrySessionEvent('sip_invite', 'info', {
      internalDialerLeg: isInternalDialerLeg,
      hotTransferId,
      remoteIdentity: parseRemoteIdentity(invitation),
    }, salesCallId || telemetryRef.current.activeCallId || null);

    if (isHotTransferLeg) {
      setState((current) => ({
        ...current,
        phase: 'hot_transfer_pending',
        message: 'Hot transfer ready in background',
        remoteIdentity: null,
      }));
    } else if (isInternalDialerLeg) {
      setState((current) => ({
        ...current,
        phase: 'calling',
        message: 'Connecting campaign call...',
        remoteIdentity: null,
      }));
    } else {
      setState((current) => ({
        ...current,
        phase: 'ringing',
        message: 'Incoming call',
        remoteIdentity: parseRemoteIdentity(invitation),
      }));
    }

    const handleSessionEnded = (eventName: string) => {
      // A replaced session can emit a late BYE/terminate; only the current owner cleans up.
      if (runtimeRef.current.session !== invitation) {
        return;
      }
      void flushPeerConnectionSample();
      stopTelemetryStats();
      activeCallRef.current = null;
      setActiveCall(null);
      localAudioMutedRef.current = false;
      setLocalAudioMutedState(false);
      runtimeRef.current.session = null;
      setPoolLineLive(false);
      sessionSalesCallIdRef.current = null;
      resetAudioElement();
      void recordTelemetrySessionEvent(eventName, 'info', {
        internalDialerLeg: isInternalDialerLeg,
      }, salesCallId || telemetryRef.current.activeCallId || null);
      // A line ending under a rebuild is the rebuild's doing; any other end
      // (the agent left the pool, the switch hung up) closes the episode.
      if (!mediaRebuildInFlightRef.current) {
        settleMediaRebuild('failed', salesCallId, 'line_ended');
        resetMediaEpisode();
      }
      if (promotePendingInvite()) {
        return;
      }
      setState((current) => ({
        ...current,
        phase: 'online',
        message: 'Call ended',
        remoteIdentity: null,
      }));
    };

    invitation.stateChange.addListener((sessionState) => {
      if (String(sessionState) === 'Established') {
        if (runtimeRef.current.session !== invitation) {
          return;
        }
        if (isInternalDialerLeg) setPoolLineLive(true);
        const sessionDescriptionHandler = (invitation as Session & {
          sessionDescriptionHandler?: { peerConnection?: RTCPeerConnection };
        }).sessionDescriptionHandler;
        const peerConnection = sessionDescriptionHandler?.peerConnection;
        if (peerConnection) {
          const audioElement = remoteAudioRef.current;
          attachPeerConnectionTelemetry(salesCallId || telemetryRef.current.activeCallId || null, peerConnection);
          console.info('[softphone] session established — ice=%s, pc=%s, audioEl=%s',
            peerConnection.iceConnectionState, peerConnection.connectionState, Boolean(audioElement));
          if (audioElement) {
            const syncRemoteStream = () => {
              const receivers = peerConnection.getReceivers();
              const audioReceivers = receivers.filter((r) => r.track.kind === 'audio');
              console.info('[softphone] syncRemoteStream — receivers=%d, audioReceivers=%d, ice=%s',
                receivers.length, audioReceivers.length, peerConnection.iceConnectionState);
              const remoteStream = new MediaStream();
              peerConnection.getReceivers().forEach((receiver) => {
                remoteStream.addTrack(receiver.track);
              });
              void applyPreferredSink(audioElement);
              audioElement.srcObject = remoteStream;
              audioElement.play().then(() => {
                console.info('[softphone] audio playing');
              }).catch((error) => {
                console.warn('[softphone] audio play() failed:', error);
                // The agent hears nothing when this fires — journal it so
                // "couldn't hear the customer" reports have a signal to match.
                void recordTelemetrySessionEvent('remote_audio_play_failed', 'warning', {
                  message: error instanceof Error ? error.message : String(error),
                }, salesCallId || telemetryRef.current.activeCallId || null);
              });
            };
            peerConnection.addEventListener('track', syncRemoteStream);
            syncRemoteStream();
          } else {
            console.warn('[softphone] no audio element ref — cannot play remote audio');
          }
        } else {
          console.warn('[softphone] session established but no peer connection');
        }

        void recordTelemetrySessionEvent('sip_session_established', 'info', {
          internalDialerLeg: isInternalDialerLeg,
          hotTransferId,
        }, salesCallId || telemetryRef.current.activeCallId || null);
        if (isInternalDialerLeg) {
          return;
        }

        setState((current) => ({
          ...current,
          phase: 'in_call',
          message: 'Call in progress',
          remoteIdentity: parseRemoteIdentity(invitation),
        }));
        return;
      }

      if (String(sessionState) === 'Terminated') {
        handleSessionEnded('sip_session_terminated');
      }
    });

    invitation.delegate = {
      onBye: () => {
        handleSessionEnded('sip_bye');
      },
    };

    if (isInternalDialerLeg) {
      // accept() is a long await — microphone acquisition plus SDP/ICE
      // negotiation — and the leg can die anywhere inside it: the agent hangs
      // up, a newer invite takes the session slot, the lease moves to another
      // tab. sip.js cannot cancel an accept in flight, so the state is re-read
      // on both sides of it. Completing the answer anyway is not merely noisy:
      // the success branch clears the media-fault breaker and resets the
      // answer-loop counter on the strength of a call that never carried audio,
      // and the 200 OK bridges the prospect into a torn-down peer connection.
      const readSessionState = () => invitation.state;
      const abortReason = (): AutoAnswerAbortReason | null => autoAnswerAbortReason({
        sessionState: readSessionState(),
        isRuntimeSession: runtimeRef.current.session === invitation,
        runtimeStopped: runtimeRef.current.stopped,
      });
      const abandonAutoAnswer = (reason: AutoAnswerAbortReason, stage: 'before_accept' | 'after_accept') => {
        void recordTelemetrySessionEvent('auto_answer_aborted_stale_session', 'warning', {
          reason,
          stage,
          sessionState: readSessionState(),
          hotTransferId,
        }, salesCallId || telemetryRef.current.activeCallId || null);
        // A leg that died during the answer emits no further state change, so
        // the session slot and the "Connecting campaign call..." card would stay
        // held by a corpse until the next snapshot poll swept it. Release them
        // here; the identity guard inside makes a later terminate event a no-op.
        // Its own event name, not sip_session_terminated: the leg can still be
        // Established at this point and it is the dispose() below that ends it,
        // so reusing the remote-hangup name would misreport who hung up.
        if (runtimeRef.current.session === invitation) {
          handleSessionEnded('sip_session_abandoned');
        }
        // dispose() is the one teardown that is correct in every state this can
        // land in: it closes the session description handler (stopping the mic
        // capture and the peer connection behind it), BYEs an answer that did
        // complete, rejects one still in Initial, and no-ops on a session that
        // is already terminated. No breaker or device-fault changes — an
        // abandoned leg is neither a success nor a media failure.
        void invitation.dispose().catch(() => undefined);
      };

      // Defensive; no async gap exists today between adoption and accept — the
      // only caller passing isInternalDialerLeg reaches here synchronously from
      // onInvite, in the same tick that claimed the session slot above.
      const staleBeforeAccept = abortReason();
      if (staleBeforeAccept) {
        abandonAutoAnswer(staleBeforeAccept, 'before_accept');
        return;
      }
      void recordTelemetrySessionEvent('auto_answer_attempt', 'info', { hotTransferId }, salesCallId || telemetryRef.current.activeCallId || null);
      void invitation.accept({
        sessionDescriptionHandlerOptions: {
          constraints: {
            audio: resolveAudioConstraints(),
            video: false,
          },
        },
      }).then(() => {
        const staleAfterAccept = abortReason();
        if (staleAfterAccept) {
          abandonAutoAnswer(staleAfterAccept, 'after_accept');
          return;
        }
        void recordTelemetrySessionEvent('auto_answer_succeeded', 'info', { hotTransferId }, salesCallId || telemetryRef.current.activeCallId || null);
        consecutiveAutoAnswerFailsRef.current = 0;
        // accept() resolving means the mic was acquired — clear any stale
        // device fault the recovery probe missed.
        if (deviceErrorRef.current) {
          genericFaultTrippedAtRef.current = null;
          applyDeviceError(null);
        }
      }).catch((error) => {
        // Only the unambiguous reasons are read here. A failing accept always
        // terminates the invitation itself (sip.js rejects the INVITE 480 and
        // transitions to Terminated), so a terminal state in this branch cannot
        // tell a lost leg apart from a mic that would not open — and treating a
        // real device fault as a benign abort would reopen the Jul 15 burn loop.
        // A session slot taken by a newer invite, or a runtime that stopped or
        // went lame-duck, have no such ambiguity: accept() failing cannot cause
        // either.
        const staleAfterAccept = abortReason();
        if (staleAfterAccept && staleAfterAccept !== 'session_terminated') {
          abandonAutoAnswer(staleAfterAccept, 'after_accept');
          return;
        }
        const message = error instanceof Error ? error.message : 'Failed to auto-answer campaign call';
        const classified = classifyMediaError(error);
        void recordTelemetrySessionEvent('auto_answer_failed', 'error', { message, kind: classified.kind, hotTransferId }, salesCallId || telemetryRef.current.activeCallId || null);
        if (classified.kind !== 'other') {
          // Sticky: a transient error phase flips back to 'online' when the
          // failed session tears down (still SIP-registered), and the dialer
          // claims the next contact — the Jul 15 one-burned-lead-per-1.3s
          // loop. Any media-acquisition fault must outlive the session; the
          // health probe clears it once the mic actually opens again.
          applyDeviceError(classified.message);
          setActionError(classified.message);
        } else {
          // Unknown cause. One failure can be a benign race (e.g. the invite
          // cancelled mid-accept), but a streak is the same burn loop wearing
          // a different error — trip the breaker anyway, with a cooldown so
          // the recovery probe can't oscillate it back into fast dialing.
          consecutiveAutoAnswerFailsRef.current += 1;
          if (consecutiveAutoAnswerFailsRef.current >= ANSWER_LOOP_TRIP_THRESHOLD) {
            void recordTelemetrySessionEvent('auto_answer_loop_tripped', 'error', {
              consecutiveFailures: consecutiveAutoAnswerFailsRef.current,
            }, null);
            genericFaultTrippedAtRef.current = Date.now();
            applyDeviceError(ANSWER_LOOP_MESSAGE);
            setActionError(ANSWER_LOOP_MESSAGE);
          }
        }
        setState((current) => ({
          ...current,
          phase: 'error',
          message: classified.kind === 'other' ? message : classified.message,
          remoteIdentity: null,
        }));
      });
    }
  }, [
    applyDeviceError,
    attachPeerConnectionTelemetry,
    flushPeerConnectionSample,
    promotePendingInvite,
    recordTelemetrySessionEvent,
    resetAudioElement,
    resetMediaEpisode,
    settleMediaRebuild,
    stopTelemetryStats,
  ]);

  useEffect(() => {
    adoptInvitationRef.current = adoptInvitation;
  }, [adoptInvitation]);

  const answer = useCallback(async () => {
    if (!hasRuntimeLeaseRef.current) {
      postOwnerCommand({ type: 'answer' });
      return;
    }
    const session = runtimeRef.current.session;
    if (!session) {
      // The ringing "Answer" card is gated on phase === 'ringing', but the invite
      // lives in runtimeRef and can be disposed by a runtime teardown while the card
      // is still up. Don't no-op silently: report it and clear the stale card so the
      // user isn't clicking a dead button, and we get telemetry when it happens.
      console.warn('[softphone] answer: no session');
      void recordTelemetrySessionEvent('answer_no_session', 'warning');
      setActionError('This call is no longer available.');
      setState((current) => (
        current.phase === 'ringing'
          ? { ...current, phase: 'online', message: 'Call ended', remoteIdentity: null }
          : current
      ));
      return;
    }
    try {
      stopTone();
      const invitation = session as Invitation;
      if (typeof invitation.accept !== 'function') {
        console.warn('[softphone] answer: session has no accept method');
        return;
      }
      console.info('[softphone] answering call, session state=%s', (session as Session & { state?: string }).state);
      // Immediately update UI so user sees "Connecting..." with spinner
      setState((current) => ({
        ...current,
        phase: 'calling',
        message: 'Connecting call...',
      }));
      await invitation.accept({
        sessionDescriptionHandlerOptions: {
          constraints: {
            audio: resolveAudioConstraints(),
            video: false,
          },
        },
      });
      void recordTelemetrySessionEvent('answer_clicked', 'info');
      setActionError(null);
      consecutiveAutoAnswerFailsRef.current = 0;
      if (deviceErrorRef.current) {
        genericFaultTrippedAtRef.current = null;
        applyDeviceError(null);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to answer call';
      // Ignore benign race when user clicks answer while SIP.js already moved to establishing/established.
      if (/invalid session state (establishing|established)/i.test(message)) {
        setActionError(null);
        return;
      }
      const classified = classifyMediaError(error);
      if (classified.kind !== 'other') {
        // Same stickiness as the auto-answer path: see adoptInvitation.
        applyDeviceError(classified.message);
      }
      setActionError(classified.kind === 'other' ? message : classified.message);
      throw error;
    }
  }, [applyDeviceError, postOwnerCommand, recordTelemetrySessionEvent, stopTone]);

  const reject = useCallback(async () => {
    if (!hasRuntimeLeaseRef.current) {
      postOwnerCommand({ type: 'reject' });
      return;
    }
    const session = runtimeRef.current.session;
    if (!session) {
      return;
    }
    try {
      stopTone();
      const invitation = session as Invitation;
      if (typeof invitation.reject === 'function') {
        // 486 (not sip.js's default 480) so the bridge fails with USER_BUSY —
        // the carrier treats busy as final instead of retrying the call.
        await invitation.reject({ statusCode: 486, reasonPhrase: 'Busy Here' });
      }
      void recordTelemetrySessionEvent('reject_clicked', 'info');
      setActionError(null);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to reject call';
      if (/invalid session state (terminated|established)/i.test(message)) {
        setActionError(null);
        return;
      }
      setActionError(message);
      throw error;
    }
  }, [postOwnerCommand, recordTelemetrySessionEvent, stopTone]);

  // intent 'agent': the agent pressed hang-up. Only those BYEs carry the
  // tag; a reload, dispose or runtime stop must read as a lost line so a
  // pool call can be rescued.
  const endRuntimeSession = useCallback(async (options: { intent?: 'agent' } = {}): Promise<boolean> => {
    const session = runtimeRef.current.session as (Session & {
      bye?: (byeOptions?: SessionByeOptions) => Promise<unknown>;
      reject?: (options?: { statusCode?: number; reasonPhrase?: string }) => Promise<void>;
      dispose?: () => Promise<void> | void;
    }) | null;
    if (!session) {
      return false;
    }
    if (typeof session.bye === 'function') {
      await session.bye(byeOptionsFor(options.intent ?? null)).catch(() => undefined);
      return true;
    }
    if (typeof session.reject === 'function') {
      await session.reject({ statusCode: 486, reasonPhrase: 'Busy Here' }).catch(() => undefined);
      return true;
    }
    if (typeof session.dispose === 'function') {
      await Promise.resolve(session.dispose()).catch(() => undefined);
      return true;
    }
    return false;
  }, []);

  const clearStaleCallUi = useCallback((message: string = 'Call ended', disposeZombieSession = false) => {
    // Don't clear the session while ringing — user needs it to answer the call.
    if (latestStateRef.current.phase === 'ringing') {
      return;
    }
    // Don't tear down an active SIP session just because the server's call store
    // has no matching record (e.g. true inbound calls that aren't campaign-tracked,
    // or a just-adopted leg whose snapshot row hasn't arrived yet — still 'Initial'
    // for the first moments before auto-answer moves it to Establishing).
    // The SIP session state is the source of truth for whether media is flowing:
    // only a terminal session may be cleared out from under the UI — UNLESS the
    // caller proved this exact call ended server-side (disposeZombieSession): a
    // BYE lost with a dead socket leaves the session 'Established' forever, and
    // protecting that corpse kept agents talking into a dead line behind a
    // "call still active" banner.
    const session = runtimeRef.current.session;
    if (session) {
      const sessionState = (session as Session & { state?: string }).state;
      if (sessionState !== 'Terminated' && sessionState !== 'Terminating') {
        if (!disposeZombieSession) {
          return;
        }
        void endRuntimeSession();
      }
    }
    // Already idle: nothing to clear. Without this guard, every no-call
    // snapshot (5s fallback poll, all day) re-runs the teardown and its state
    // writes for a state that never changed.
    if (
      !session
      && !activeCallRef.current
      && !telemetryRef.current.activeCallId
      && telemetryRef.current.statsTimer === null
      && latestStateRef.current.phase !== 'in_call'
      && latestStateRef.current.phase !== 'calling'
    ) {
      return;
    }
    stopTelemetryStats();
    telemetryRef.current.activeCallId = null;
    activeCallRef.current = null;
    setActiveCall(null);
    localAudioMutedRef.current = false;
    setLocalAudioMutedState(false);
    runtimeRef.current.session = null;
    setPoolLineLive(false);
    sessionSalesCallIdRef.current = null;
    resetAudioElement();
    setState((current) => {
      if (
        current.phase !== 'in_call'
        && !(current.phase === 'calling' && phoneRegisteredRef.current)
      ) {
        return current;
      }
      return {
        ...current,
        phase: 'online',
        message,
        remoteIdentity: null,
      };
    });
  }, [endRuntimeSession, resetAudioElement, stopTelemetryStats]);

  // Shared no-current-call handler for every server snapshot (SSE stream,
  // fallback poll, stale-stream reconcile): a snapshot explicitly reporting
  // the session's own call as ended means the server hung it up while our
  // socket was down and the BYE never reached us — dispose the zombie instead
  // of protecting it. (Mere absence is not proof: a just-adopted leg can lag
  // the snapshot by a few seconds.)
  const clearAbsentCallUi = useCallback((calls: CallSummary[]) => {
    const sessionCallId = sessionSalesCallIdRef.current;
    const endedServerSide = Boolean(
      sessionCallId && calls.some((call) => call.callId === sessionCallId && call.status === 'ended')
    );
    clearStaleCallUi('Call ended', endedServerSide);
  }, [clearStaleCallUi]);

  const hangup = useCallback(async () => {
    if (!hasRuntimeLeaseRef.current) {
      postOwnerCommand({ type: 'hangup' });
      return;
    }
    try {
      stopTone();
      if (activeCall) {
        try {
          void recordTelemetrySessionEvent('hangup_clicked', 'info', {}, activeCall.callId);
          await sipApi.hangupCall(activeCall.callId);
          setActionError(null);
          return;
        } catch (error) {
          const endedLocally = await endRuntimeSession({ intent: 'agent' });
          if (!endedLocally) {
            // No live SIP session means no media is flowing: the tracked "active
            // call" is a stale snapshot (SSE/poll lag on a slow connection) and the
            // server ends calls independently of this request. Clear the dead call
            // UI instead of stranding the agent on it with an error.
            void recordTelemetrySessionEvent('hangup_cleared_stale_call', 'warning', {
              message: error instanceof Error ? error.message : 'hangup request failed',
            }, activeCall.callId);
            clearStaleCallUi();
          }
          setActionError(null);
          return;
        }
      }

      const endedLocally = await endRuntimeSession({ intent: 'agent' });
      if (!endedLocally) {
        return;
      }
      void recordTelemetrySessionEvent('hangup_clicked', 'info');
      setActionError(null);
    } catch (error) {
      setActionError(error instanceof Error ? error.message : 'Failed to hang up');
      throw error;
    }
  }, [activeCall, clearStaleCallUi, endRuntimeSession, postOwnerCommand, recordTelemetrySessionEvent, stopTone]);

  const acceptIncomingCall = useCallback(async () => {
    if (!hasRuntimeLeaseRef.current) {
      postOwnerCommand({ type: 'accept_incoming' });
      return;
    }
    const pending = pendingInviteRef.current;
    if (!pending) {
      return;
    }
    pendingInviteRef.current = null;
    setIncomingCall(null);
    const pendingCallId = extractSalesCallHeader(pending);
    void recordTelemetrySessionEvent('incoming_accept_clicked', 'info', {}, pendingCallId);
    try {
      const current = activeCallRef.current;
      // Never hang up the call we are about to answer (the snapshot can have
      // already promoted the incoming call to activeCall if the old call just ended).
      if (current && current.status !== 'ended' && current.callId !== pendingCallId) {
        await sipApi.hangupCall(current.callId).catch(() => endRuntimeSession({ intent: 'agent' }));
      } else {
        await endRuntimeSession({ intent: 'agent' });
      }
    } catch {
      // Answering the new call matters more than how the old one died.
    }
    adoptInvitationRef.current?.(pending, {
      salesCallId: pendingCallId,
      isInternalDialerLeg: false,
    });
    await answer();
  }, [answer, endRuntimeSession, postOwnerCommand, recordTelemetrySessionEvent]);

  const declineIncomingCall = useCallback(async () => {
    if (!hasRuntimeLeaseRef.current) {
      postOwnerCommand({ type: 'decline_incoming' });
      return;
    }
    const pending = pendingInviteRef.current;
    pendingInviteRef.current = null;
    setIncomingCall(null);
    if (!pending) {
      return;
    }
    void recordTelemetrySessionEvent('incoming_decline_clicked', 'info', {}, extractSalesCallHeader(pending));
    await pending.reject({ statusCode: 486, reasonPhrase: 'Busy Here' }).catch(() => undefined);
  }, [postOwnerCommand, recordTelemetrySessionEvent]);

  // Shared by the local dial() and dial commands routed from other tabs — the
  // routed form must carry its own destination instead of reading this tab's
  // dial box.
  const dialTo = useCallback(async (destination: string): Promise<void> => {
    try {
      const normalizedDestination = normalizePhoneLike(destination);
      if (!normalizedDestination) {
        throw new Error('destination is required');
      }
      if (activeCallRef.current) {
        throw new Error('you already have an active call');
      }
      if (!enabledForRole) {
        throw new Error('browser phone is not enabled for this role');
      }
      if (latestStateRef.current.phase !== 'online') {
        throw new Error('phone is not online');
      }

      const response = await sipApi.originateCall({
        destination: normalizedDestination,
      });
      void recordTelemetrySessionEvent('dial_requested', 'info', {
        destination: normalizedDestination,
      });
      // Track the call immediately so the ringback tone starts without waiting
      // for the next call-snapshot poll.
      activeCallRef.current = response.call;
      setActiveCall(response.call);
      setActionError(null);
      setDialDestination(normalizedDestination);
      setState((current) => ({
        ...current,
        phase: 'calling',
        message: `Dialing ${normalizedDestination}...`,
        remoteIdentity: null,
      }));
    } catch (error) {
      setActionError(error instanceof Error ? error.message : 'Failed to start call');
      throw error;
    }
  }, [enabledForRole, recordTelemetrySessionEvent]);

  const dial = useCallback(async (): Promise<void> => {
    if (!hasRuntimeLeaseRef.current) {
      // The owner validates and dials; failures surface via the mirrored
      // actionError rather than a local throw.
      postOwnerCommand({ type: 'dial', destination: dialDestination });
      return;
    }
    await dialTo(dialDestination);
  }, [dialDestination, dialTo, postOwnerCommand]);

  const sendDtmf = useCallback((tone: string) => {
    if (!hasRuntimeLeaseRef.current) {
      postOwnerCommand({ type: 'send_dtmf', tone });
      return;
    }
    const session = runtimeRef.current.session;
    if (!session) {
      console.warn('[softphone] sendDtmf: no active session');
      return;
    }

    // Primary: RFC 2833 via RTCDTMFSender (in-band RTP telephone-event).
    // Most reliable for WebRTC — FreeSWITCH natively detects and relays.
    const sdh = (session as Session & {
      sessionDescriptionHandler?: { peerConnection?: RTCPeerConnection };
    }).sessionDescriptionHandler;
    const pc = sdh?.peerConnection;
    if (pc) {
      const audioSender = pc.getSenders().find((s) => s.track?.kind === 'audio');
      if (audioSender?.dtmf) {
        try {
          audioSender.dtmf.insertDTMF(tone, 160, 80);
          return;
        } catch (error) {
          console.warn('[softphone] insertDTMF failed, trying SIP INFO fallback:', error);
        }
      } else {
        console.warn('[softphone] no DTMF-capable audio sender, trying SIP INFO fallback');
      }
    } else {
      console.warn('[softphone] no peer connection, trying SIP INFO fallback');
    }

    // Fallback: SIP INFO with application/dtmf-relay.
    const sipSession = session as Session & {
      info?: (options: {
        requestOptions: {
          body: { contentType: string; content: string };
        };
      }) => Promise<void>;
    };
    if (typeof sipSession.info === 'function') {
      void sipSession.info({
        requestOptions: {
          body: {
            contentType: 'application/dtmf-relay',
            content: `Signal=${tone}\r\nDuration=160`,
          },
        },
      }).catch((error) => {
        console.warn('[softphone] SIP INFO DTMF failed:', error);
      });
    } else {
      console.warn('[softphone] sendDtmf: no DTMF method available');
    }
  }, [postOwnerCommand]);

  const clearActionError = useCallback(() => {
    if (!hasRuntimeLeaseRef.current) {
      // actionError is mirrored from the owner; clearing only locally would be
      // undone by the next snapshot.
      postOwnerCommand({ type: 'clear_action_error' });
    }
    setActionError(null);
  }, [postOwnerCommand]);

  const syncIdentityForCampaign = useCallback(async (campaignId: string | null | undefined): Promise<void> => {
    if (!enabledForRole) {
      return;
    }
    const normalizedCampaignId = String(campaignId || '').trim();
    if (!normalizedCampaignId) {
      return;
    }
    if (!hasRuntimeLeaseRef.current) {
      // Identity (campaign caller-ID) belongs to the phone runtime; the owner
      // resolves it and the result comes back on the snapshot.
      postOwnerCommand({ type: 'sync_identity', campaignId: normalizedCampaignId });
      return;
    }

    const requestId = identitySyncRequestRef.current + 1;
    identitySyncRequestRef.current = requestId;

    try {
      const credentials = await sipApi.getPhoneCredentials({ campaignId: normalizedCampaignId });
      if (identitySyncRequestRef.current !== requestId) {
        return;
      }
      mediaRebuildEnabledRef.current = Boolean(credentials.mediaRebuild);
      if (!credentials.enabled) {
        setPrimaryDidNumber(null);
        setActionError(disabledReasonMessage(credentials.reason));
        return;
      }

      const normalizedDid = normalizePhoneLike(String(credentials.primaryDidNumber || ''));
      if (!normalizedDid && !credentials.poolAvailable) {
        setPrimaryDidNumber(null);
        setActionError('No active dialing DID is configured for this campaign.');
        return;
      }

      // Pool-only agents have no primary DID; the pool assigns one per call.
      setPrimaryDidNumber(credentials.primaryDidNumber || null);
      setActionError(null);
    } catch (error) {
      setPrimaryDidNumber(null);
      setActionError(error instanceof Error ? error.message : 'Failed to resolve campaign dialing identity.');
    }
  }, [enabledForRole, postOwnerCommand]);

  // ---- Cross-tab mirror & command routing (see softphone-tab-sync.ts) ------
  // The lease owner is the only tab with a SIP runtime. It broadcasts its
  // phone state so every other tab renders a live phone UI, and executes the
  // commands those tabs forward. This replaces the old focus-follows-phone
  // model where each tab switch rebuilt the runtime and re-REGISTERed the
  // extension (single-registration profile = the binding flapped between
  // tabs on every switch).

  const publishBusSnapshot = useCallback(() => {
    const bus = busRef.current;
    if (!bus || !hasRuntimeLeaseRef.current) {
      return;
    }
    bus.post({
      kind: 'snapshot',
      ownerTabId: tabIdRef.current,
      // The presented state (device faults coerced to 'error') so mirror tabs
      // can't show an answerable phone the owner knows is mic-less.
      state: presentedState,
      activeCall,
      recentCalls,
      incomingCall,
      localAudioMuted,
      reconnecting,
      mediaFailed,
      silentUplink,
      primaryDidNumber,
      actionError,
      // Already past the grace window on this tab's clock — a mirror has no
      // registerer to time one of its own.
      registration: registrationFault,
    });
  }, [
    actionError,
    activeCall,
    registrationFault,
    incomingCall,
    localAudioMuted,
    mediaFailed,
    primaryDidNumber,
    presentedState,
    recentCalls,
    reconnecting,
    silentUplink,
  ]);

  const applyBusSnapshot = useCallback((snapshot: SoftphoneTabSnapshot) => {
    if (hasRuntimeLeaseRef.current) {
      return;
    }
    // A make-before-break lame duck still IS the phone — don't overwrite its UI
    // with the winner's mirror; the post-teardown hello re-syncs it.
    if (isLameDuck()) {
      return;
    }
    // Content-compare before every set: each broadcast delivers fresh object
    // identities, and unconditional sets would re-render the whole dialer
    // workspace once per owner-side change (see isSameCallSnapshot).
    setState((current) => (
      current.phase === snapshot.state.phase
      && current.extension === snapshot.state.extension
      && current.message === snapshot.state.message
      && current.remoteIdentity === snapshot.state.remoteIdentity
        ? current
        : snapshot.state
    ));
    if (!isSameCallSnapshot(activeCallRef.current, snapshot.activeCall)) {
      activeCallRef.current = snapshot.activeCall;
      setActiveCall(snapshot.activeCall);
    }
    publishRecentCalls(snapshot.recentCalls);
    setIncomingCall((current) => (
      current?.callId === snapshot.incomingCall?.callId
      && current?.remoteIdentity === snapshot.incomingCall?.remoteIdentity
        ? current
        : snapshot.incomingCall
    ));
    localAudioMutedRef.current = snapshot.localAudioMuted;
    setLocalAudioMutedState(snapshot.localAudioMuted);
    setReconnecting(snapshot.reconnecting);
    // An owner on the previous build sends none.
    setMediaFailed(snapshot.mediaFailed ?? false);
    setSilentUplink(snapshot.silentUplink ?? false);
    mirroredRegistrationRef.current = snapshot.registration;
    setPrimaryDidNumber(snapshot.primaryDidNumber);
    setActionError(snapshot.actionError);
  }, [isLameDuck, publishRecentCalls]);

  // Pool-driven re-REGISTER. FreeSWITCH can expire a binding without telling
  // the client (an OPTIONS-ping failure keyed to the reused registration after
  // a reconnect-in-place), so the Registerer sits "Registered" while the
  // switch has no line — the pool sees that first, holds the seat, and asks
  // here. REGISTER is an idempotent replace: repairing a binding that was fine
  // costs one message. The registerer is read at call time, never captured —
  // a reconnect swaps it — and a reconnect in flight is left to do its own.
  const lastReRegisterNudgeAtRef = useRef(0);
  const reRegisterNow = useCallback((reason: string) => {
    const runtime = runtimeRef.current;
    // A stuck-media rebuild's un-REGISTER must land before any REGISTER: the
    // pool reaps the dead leg on it.
    if (!runtime.registerer || runtime.stopped || runtime.muted || reconnectInFlightRef.current || mediaRebuildInFlightRef.current) {
      return;
    }
    if (readTransportState() !== 'connected') {
      return;
    }
    const now = Date.now();
    if (now - lastReRegisterNudgeAtRef.current < REREGISTER_NUDGE_MIN_INTERVAL_MS) {
      return;
    }
    lastReRegisterNudgeAtRef.current = now;
    void recordTelemetrySessionEvent('reregister_nudged_by_pool', 'info', {
      reason,
      registered: phoneRegisteredRef.current,
    });
    // A rejection here is RequestPendingError: a REGISTER is already in
    // flight (up to 32s after a reconnect), which is the nudge happening.
    void runtime.registerer.register().then(() => undefined).catch(() => undefined);
  }, [readTransportState, recordTelemetrySessionEvent]);

  const handleBusCommand = useCallback((command: SoftphoneTabCommand) => {
    // Every handler below runs with the lease held (the subscriber checks), so
    // the public actions take their local paths. Errors already land in
    // actionError and mirror back to the sender — nothing to rethrow here.
    switch (command.type) {
      case 'dial':
        setDialDestination(command.destination);
        void dialTo(command.destination).catch(() => undefined);
        return;
      case 'answer':
        void answer().catch(() => undefined);
        return;
      case 'reject':
        void reject().catch(() => undefined);
        return;
      case 'hangup':
        void hangup().catch(() => undefined);
        return;
      case 'accept_incoming':
        void acceptIncomingCall().catch(() => undefined);
        return;
      case 'decline_incoming':
        void declineIncomingCall().catch(() => undefined);
        return;
      case 'send_dtmf':
        sendDtmf(command.tone);
        return;
      case 'set_muted':
        setLocalAudioMuted(command.muted);
        return;
      case 'set_audio_input':
        void setAudioInputDevice(command.deviceId).catch(() => undefined);
        return;
      case 'set_audio_output':
        void setAudioOutputDevice(command.deviceId).catch(() => undefined);
        return;
      case 'sync_identity':
        void syncIdentityForCampaign(command.campaignId);
        return;
      case 'refresh_snapshot':
        void refreshCallSnapshot({ force: command.force });
        return;
      case 'clear_action_error':
        setActionError(null);
        return;
      case 'reregister':
        reRegisterNow(command.reason);
        return;
      case 'reconnect':
        void recordTelemetrySessionEvent('manual_reconnect', 'info', {});
        restartPhoneNow();
        return;
      case 'telemetry':
        void recordTelemetrySessionEvent(command.eventName, command.severity, command.payload ?? {}, command.callId ?? null);
    }
  }, [
    acceptIncomingCall,
    answer,
    declineIncomingCall,
    dialTo,
    hangup,
    reRegisterNow,
    recordTelemetrySessionEvent,
    refreshCallSnapshot,
    restartPhoneNow,
    reject,
    sendDtmf,
    setAudioInputDevice,
    setAudioOutputDevice,
    setLocalAudioMuted,
    syncIdentityForCampaign,
  ]);

  useEffect(() => {
    publishBusSnapshotRef.current = publishBusSnapshot;
  }, [publishBusSnapshot]);

  useEffect(() => {
    applyBusSnapshotRef.current = applyBusSnapshot;
  }, [applyBusSnapshot]);

  useEffect(() => {
    handleBusCommandRef.current = handleBusCommand;
  }, [handleBusCommand]);

  useEffect(() => {
    if (!enabledForRole) {
      return undefined;
    }
    const bus = openSoftphoneTabBus();
    busRef.current = bus;
    if (!bus) {
      return undefined;
    }
    const unsubscribe = bus.subscribe((message) => {
      if (message.kind === 'hello') {
        // publishBusSnapshot no-ops without the lease, so only the owner answers.
        publishBusSnapshotRef.current?.();
        return;
      }
      if (message.kind === 'command') {
        if (hasRuntimeLeaseRef.current) {
          handleBusCommandRef.current?.(message.command);
        }
        return;
      }
      applyBusSnapshotRef.current?.(message);
    });
    return () => {
      unsubscribe();
      bus.close();
      busRef.current = null;
    };
  }, [enabledForRole]);

  // Owner: broadcast whenever any mirrored field changes (and once on lease
  // acquisition — publishBusSnapshot's identity covers both).
  useEffect(() => {
    if (!enabledForRole || !hasRuntimeLease) {
      return;
    }
    publishBusSnapshot();
  }, [enabledForRole, hasRuntimeLease, publishBusSnapshot]);

  // Mirror tabs announce themselves so the owner pushes its current state —
  // on mount and again whenever this tab loses the lease.
  useEffect(() => {
    if (!enabledForRole || hasRuntimeLease) {
      return;
    }
    busRef.current?.post({ kind: 'hello', tabId: tabIdRef.current });
  }, [enabledForRole, hasRuntimeLease]);

  // oxlint-disable-next-line max-lines-per-function -- frozen baseline (structural-gates rollout)
  useEffect(() => {
    if (!enabledForRole) {
      void stopRuntime({ endTelemetry: true });
      stopTone();
      setPrimaryDidNumber(null);
      activeCallRef.current = null;
      setActiveCall(null);
      setActionError(null);
      setState({
        phase: 'disabled',
        extension: null,
        message: 'Softphone not required for this role.',
        remoteIdentity: null,
      });
      return undefined;
    }

    if (!hasRuntimeLease) {
      if (isLameDuck()) {
        // Make-before-break: another tab owns the lease now, but this runtime
        // still holds the live FS registration. Keep it (and any live call)
        // until the winner reports online — its REGISTER atomically replaces
        // our binding — or the grace cap fires. The extension is never left
        // unregistered during a tab switch. State stays as-is: this tab
        // genuinely is the phone until the handoff completes.
        beginLeaseHandoff();
        return undefined;
      }
      void stopRuntime({ endTelemetry: true });
      stopTone();
      activeCallRef.current = null;
      setActiveCall(null);
      setActionError(null);
      setState({
        phase: 'idle',
        extension: null,
        message: 'Browser phone is active in another tab.',
        remoteIdentity: null,
      });
      return undefined;
    }

    let cancelled = false;
    const isCancelled = () => cancelled;

    // oxlint-disable-next-line max-lines-per-function -- frozen baseline (structural-gates rollout)
    async function startPhone(): Promise<void> {
      // Lease regained while the previous runtime is still alive in this tab
      // (make-before-break lame duck): reuse it. The registration never
      // dropped, so the phone is online the instant the user switches back.
      if (
        leaseHandoffRef.current.active
        && runtimeRef.current.userAgent
        && !runtimeRef.current.stopped
        && phoneRegisteredRef.current
      ) {
        cancelLeaseHandoff();
        runtimeRef.current.muted = false;
        // A record mirrored from the other tab must not be republished on a
        // phone that never lost its registration.
        // Publish 'online' right away: if the other tab registered while it
        // briefly held the lease, this makes it finish its own handoff
        // immediately, and its reassert nudge re-REGISTERs us (atomic replace).
        if (hasRuntimeLeaseRef.current) {
          writeSoftphoneOwnerLease({ ...buildOwnerLease(), phase: 'online' });
        }
        void recordTelemetrySessionEvent('lease_handoff_reclaimed', 'info', {});
        return;
      }
      cancelLeaseHandoff();
      runtimeRef.current.muted = false;
      // Warm the sip.js chunk while credentials resolve.
      void loadSipModule().catch(() => undefined);

      let assignedExtension: string | null = null;
      // This attempt's UA (not runtimeRef's, which may still be a previous
      // runtime when the credentials fetch throws): the catch checks whether
      // its transport came up before blaming the agent's link.
      let userAgent: UserAgent | null = null;
      runtimeRef.current.stopped = false;
      // One charge per start attempt, whatever this attempt ends up arming.
      reconnectAttemptsRef.current += 1;
      // Starts the clock the bars read: from here the phone has no binding, and
      // if this attempt never reaches Registered nothing else will say so.
      markPhoneRegistered(false);
      setPrimaryDidNumber(null);
      setState({
        phase: 'connecting',
        extension: null,
        message: 'Starting browser phone...',
        remoteIdentity: null,
      });

      try {
        const cachedCredentials = credentialsCacheRef.current;
        let credentials: SoftphoneCredentialsResponse;
        if (cachedCredentials && Date.now() - cachedCredentials.fetchedAt < CREDENTIALS_CACHE_TTL_MS) {
          credentials = cachedCredentials.value;
          // Revalidate in the background: a material change (rotated secret,
          // reassigned extension, new WSS host, phone disabled) rebuilds.
          void sipApi.getPhoneCredentials().then((fresh) => {
            credentialsCacheRef.current = { value: fresh, fetchedAt: Date.now() };
            // Not a material change: the switch applies without a rebuild.
            mediaRebuildEnabledRef.current = Boolean(fresh.mediaRebuild);
            if (isCancelled() || runtimeRef.current.stopped) {
              return;
            }
            if (credentialsMateriallyDiffer(credentials, fresh)) {
              scheduleReconnect();
            }
          }).catch(() => undefined);
        } else {
          // The one step of the start sequence that used to be unbounded, which
          // parked the UI on "Starting browser phone..." for as long as the
          // request hung (minutes, in production) while the SIP steps after it
          // were already bounded. The bound now lives in sipApi, so every
          // caller gets it; failing here lands in the catch below, which
          // reports the phone and schedules the retry.
          credentials = await sipApi.getPhoneCredentials();
          credentialsCacheRef.current = { value: credentials, fetchedAt: Date.now() };
        }
        if (!credentials.enabled) {
          await stopRuntime({ endTelemetry: true });
          setPrimaryDidNumber(null);
          setState({
            phase: 'disabled',
            extension: null,
            message: disabledReasonMessage(credentials.reason),
            remoteIdentity: null,
          });
          return;
        }

        if (!credentials.sipUsername || !credentials.sipDomain || !credentials.sipPassword || !credentials.websocketUrl || !credentials.trace) {
          throw new Error('softphone credentials are incomplete');
        }
        assignedExtension = credentials.extension || credentials.sipUsername || null;
        mediaRebuildEnabledRef.current = Boolean(credentials.mediaRebuild);
        setPrimaryDidNumber(credentials.primaryDidNumber || null);
        setState({
          phase: 'connecting',
          extension: assignedExtension,
          message: 'Connecting extension...',
          remoteIdentity: null,
        });
        beginTelemetrySession(assignedExtension, credentials.trace);

        // Proactive microphone checks, both cheap enough to run before every
        // runtime build. First: device PRESENCE — permission-granted does not
        // imply device-present (Chrome keeps "granted" with the headset
        // unplugged), and registering a phone that cannot capture audio is
        // what fed the Jul 15 dial-burn loop. Don't register; the devicechange
        // recovery effect rebuilds the runtime when a mic appears.
        if (!(await hasAudioInputDevice())) {
          void recordTelemetrySessionEvent('microphone_missing', 'error', { at: 'startup' }, null);
          applyDeviceError(NO_MICROPHONE_MESSAGE);
          setActionError(NO_MICROPHONE_MESSAGE);
          setState({
            phase: 'error',
            extension: assignedExtension,
            message: 'No microphone detected',
            remoteIdentity: null,
          });
          // No retry runs from here; a stale "reconnects automatically" hint
          // would contradict the device fault.
          return;
        }
        applyDeviceError(null);
        // Second: surface permission denials by prompting. Skipped when
        // permission is already granted, because getUserMedia spins up the
        // input device (seconds on some headsets) on every runtime rebuild.
        let micCheckNeeded = true;
        try {
          const micPermission = await navigator.permissions.query({ name: 'microphone' as PermissionName });
          micCheckNeeded = micPermission.state !== 'granted';
        } catch {
          // Permissions API unavailable — fall back to prompting.
        }
        if (micCheckNeeded) {
          try {
            const micStream = await navigator.mediaDevices.getUserMedia({ audio: true });
            micStream.getTracks().forEach((t) => t.stop());
          } catch (micError) {
            const micMsg = micError instanceof Error ? micError.message : 'Microphone access denied';
            const classified = classifyMediaError(micError);
            void recordTelemetrySessionEvent('microphone_denied', 'error', { message: micMsg, kind: classified.kind }, null);
            if (classified.kind !== 'other') {
              // Sticky for every media-fault kind — without it a busy mic at
              // startup would strand the phone in error with no recovery
              // probe watching (this path returns without registering).
              applyDeviceError(classified.message);
            }
            setActionError(classified.message);
            setState({
              phase: 'error',
              extension: assignedExtension,
              message: classified.kind === 'no-device' ? 'No microphone detected' : 'Microphone access denied',
              remoteIdentity: null,
            });
            return;
          }
        }
        if (isCancelled()) {
          return;
        }

        const sip = await loadSipModule();
        if (isCancelled()) {
          return;
        }
        await stopRuntime();
        runtimeRef.current.stopped = false;

        const uri = sip.UserAgent.makeURI(`sip:${credentials.sipUsername}@${credentials.sipDomain}`);
        if (!uri) {
          throw new Error('invalid SIP URI for browser phone');
        }

        // Relay-forced agents (users.force_turn_relay) get relay-pinned ICE so
        // a lossy direct route can never win the candidate race — unless this
        // page already proved the relay unreachable, in which case direct is
        // the only path that completes calls at all.
        const relayForced = Boolean(credentials.forceRelay) && !relayFallbackRef.current;
        relayActiveRef.current = relayForced;
        if (credentials.forceRelay && relayFallbackRef.current) {
          void recordTelemetrySessionEvent('relay_fallback_direct_boot', 'warning', {}, null);
        }
        const peerConnectionConfiguration = credentials.iceServers?.length
          ? {
              iceServers: credentials.iceServers.map((server) => ({
                urls: server.urls,
                username: server.username,
                credential: server.credential,
              })),
              ...(relayForced ? { iceTransportPolicy: 'relay' as RTCIceTransportPolicy } : {}),
            }
          : undefined;
        // Relay credentials are time-limited; a tab left open past their TTL
        // dead-airs every call. Rebuild (fresh credential fetch) well before
        // expiry — the rebuild is the same graceful path a reconnect takes.
        if (relayForced && typeof credentials.iceServersExpiresAt === 'number') {
          if (relayCredentialTimerRef.current !== null) {
            window.clearTimeout(relayCredentialTimerRef.current);
          }
          relayCredentialTimerRef.current = window.setTimeout(
            () => scheduleReconnect(),
            Math.max(60_000, credentials.iceServersExpiresAt - Date.now() - RELAY_CREDENTIAL_REFRESH_MARGIN_MS)
          );
        }

        userAgent = new sip.UserAgent({
          uri,
          displayName: credentials.displayName || credentials.sipUsername,
          authorizationUsername: credentials.sipUsername,
          authorizationPassword: credentials.sipPassword,
          transportOptions: {
            server: credentials.websocketUrl,
            connectionTimeout: 8,
            keepAliveInterval: SIP_KEEPALIVE_INTERVAL_S,
          },
          // NET-227 backstop: bounded auto-reconnection.
          reconnectionAttempts: RECONNECT_IN_PLACE_ATTEMPTS,
          reconnectionDelay: RECONNECT_IN_PLACE_DELAY_MS / 1000,
          sessionDescriptionHandlerFactoryOptions: {
            constraints: {
              // Runtime-build-time default; the accept() call sites re-resolve
              // per call so a device picked mid-session applies to the next call.
              audio: resolveAudioConstraints(),
              video: false,
            },
            peerConnectionConfiguration,
            // Cap how long an answer waits for ICE gathering before sending the 200 OK. The sip.js
            // default (5000ms) is the fleet's historical ~5s auto-answer mode: when a STUN/TURN server
            // is unreachable, gathering never completes and every leg answer eats the full timeout as
            // dead air. Healthy gathering (incl. a TURN relay allocation) finishes well under 1s, so
            // 2s keeps the relay candidate with headroom while bounding a TURN outage to +2s.
            iceGatheringTimeout: 2000,
          },
          delegate: {
            onInvite: (invitation) => {
              const salesCallId = extractSalesCallHeader(invitation);
              const hotTransferId = extractHotTransferHeader(invitation);
              const currentActiveCall = activeCallRef.current;
              const telemetryCallId = salesCallId || currentActiveCall?.callId || telemetryRef.current.activeCallId || null;
              void recordTelemetrySessionEvent('sip_invite_received_raw', 'info', {
                hasSalesCallId: Boolean(salesCallId),
                hotTransferId,
                phase: latestStateRef.current.phase,
                activeCallId: currentActiveCall?.callId || null,
                hasRuntimeSession: Boolean(runtimeRef.current.session),
                remoteIdentity: parseRemoteIdentity(invitation),
              }, telemetryCallId);

              try {
                const { isInboundCustomerCall, isInternalDialerLeg } = classifyInviteLeg({
                  salesCallId,
                  hasInboundHeader: isInboundLegInvite(invitation),
                });
                const existingSession = runtimeRef.current.session;
                const existingSessionState = (existingSession as (Session & { state?: string }) | null)?.state;
                const hasExistingSession = Boolean(
                  existingSession
                  && existingSessionState !== sip.SessionState.Terminated
                );
                // Inbound bridge legs carry the tracked call id, so the agent's own
                // incoming call no longer reads as "busy with another call".
                const inviteMatchesCurrentCall = Boolean(salesCallId && currentActiveCall?.callId === salesCallId);
                const busyPhase =
                  latestStateRef.current.phase === 'ringing'
                  || latestStateRef.current.phase === 'in_call'
                  || (!isInternalDialerLeg && latestStateRef.current.phase === 'calling');
                const busyWithTrackedCall = Boolean(currentActiveCall && currentActiveCall.status !== 'ended');
                // Busy rules live in isInviteBusy (pure, unit-tested); notably a stale
                // tracked call / stale phase does not block an internal dialer leg.
                // A lame-duck runtime (lease handed off, binding kept warm for the
                // winner) must never auto-answer: nobody is looking at this tab.
                // Busy-reject lets the dialer treat it as a normal miss instead of
                // bridging a prospect into a silent hidden tab.
                const busy = runtimeRef.current.muted || isInviteBusy({
                  isInternalDialerLeg,
                  inviteMatchesCurrentCall,
                  hasExistingSession,
                  hasPendingInvite: Boolean(pendingInviteRef.current),
                  trackedCallActive: busyWithTrackedCall,
                  phase: latestStateRef.current.phase,
                });

                if (!busy) {
                  if (isInternalDialerLeg && (busyWithTrackedCall || busyPhase)) {
                    void recordTelemetrySessionEvent('sip_invite_accepted_stale_state', 'info', {
                      staleActiveCallId: currentActiveCall?.callId || null,
                      phase: latestStateRef.current.phase,
                    }, telemetryCallId);
                  }
                  const adoptInvitationHandler = adoptInvitationRef.current;
                  if (!adoptInvitationHandler) {
                    void recordTelemetrySessionEvent('sip_invite_adopt_handler_missing', 'error', {
                      internalDialerLeg: isInternalDialerLeg,
                      hotTransferId,
                      phase: latestStateRef.current.phase,
                    }, telemetryCallId);
                    return;
                  }
                  adoptInvitationHandler(invitation, { salesCallId, isInternalDialerLeg });
                  return;
                }

                // Customer call while on another call: hold it as call waiting so the
                // agent can decide, instead of silently rejecting with 486.
                const canHoldAsCallWaiting =
                  isInboundCustomerCall
                  && !pendingInviteRef.current
                  && latestStateRef.current.phase !== 'ringing';
                if (canHoldAsCallWaiting) {
                  pendingInviteRef.current = invitation;
                  setIncomingCall({
                    callId: salesCallId,
                    remoteIdentity: parseRemoteIdentity(invitation),
                  });
                  void recordTelemetrySessionEvent('sip_invite_call_waiting', 'info', {
                    remoteIdentity: parseRemoteIdentity(invitation),
                    activeCallId: currentActiveCall?.callId || null,
                    phase: latestStateRef.current.phase,
                  }, telemetryCallId);
                  invitation.stateChange.addListener((sessionState) => {
                    // Caller hung up (CANCEL) before the agent decided.
                    if (sessionState === sip.SessionState.Terminated && pendingInviteRef.current === invitation) {
                      pendingInviteRef.current = null;
                      setIncomingCall(null);
                    }
                  });
                  return;
                }

                void recordTelemetrySessionEvent('sip_invite_rejected_busy', 'warning', {
                  internalDialerLeg: isInternalDialerLeg,
                  remoteIdentity: parseRemoteIdentity(invitation),
                  existingSessionState: existingSessionState || null,
                  activeCallId: currentActiveCall?.callId || null,
                  phase: latestStateRef.current.phase,
                }, telemetryCallId);
                void invitation.reject({
                  statusCode: 486,
                  reasonPhrase: 'Busy Here',
                }).catch((error) => {
                  console.warn('[softphone] busy invite reject failed:', error);
                });
              } catch (error) {
                const message = error instanceof Error ? error.message : 'SIP invite handler failed';
                void recordTelemetrySessionEvent('sip_invite_handler_error', 'error', {
                  message,
                  hotTransferId,
                  phase: latestStateRef.current.phase,
                }, telemetryCallId);
                console.error('[softphone] SIP invite handler failed:', error);
              }
            },
          },
        });
        userAgent.transport.onDisconnect = (error?: Error) => {
          // Identity + muted check (not the per-effect-run `cancelled` flag):
          // these listeners must survive a lame-duck handoff that gets
          // reclaimed, and must stay dead once a newer runtime replaces this
          // UserAgent.
          if (runtimeRef.current.stopped || runtimeRef.current.muted || runtimeRef.current.userAgent !== userAgent) {
            return;
          }
          transportDropsRef.current += 1;
          void recordTelemetrySessionEvent('transport_disconnected', 'warning', {
            message: error?.message || 'Transport disconnected',
          }, telemetryRef.current.activeCallId);
          // NET-227: with a live call, heal the transport in place instead of the
          // nonce-bump rebuild that would dispose the call. Lease-gated so a tab
          // that lost the runtime lease never reconnects.
          if (hasRuntimeLeaseRef.current && hasLiveSession()) {
            void reconnectInPlace();
            return;
          }
          setState((current) => ({
            ...current,
            phase: 'connecting',
            extension: assignedExtension,
            message: error?.message || 'Transport disconnected. Reconnecting...',
            remoteIdentity: null,
          }));
          scheduleReconnect();
        };

        const createRegisterer = (agent: UserAgent): Registerer => {
          const registerer = new sip.Registerer(agent, { expires: 120 });

          registerer.stateChange.addListener((registerState) => {
            if (runtimeRef.current.stopped || runtimeRef.current.muted || runtimeRef.current.registerer !== registerer) {
              return;
            }
            if (registerState === sip.RegistererState.Registered) {
              markPhoneRegistered(true);
              void recordTelemetrySessionEvent('registration_state', 'info', { state: 'registered' }, telemetryRef.current.activeCallId);
              // A fresh Registerer after a mid-call transport heal reaches
              // Registered while the call is still up: keep the call UI, only
              // the connectivity message changes.
              setState((current) => (hasLiveSession()
                ? { ...current, message: 'Phone online' }
                : {
                  phase: 'online',
                  extension: assignedExtension,
                  message: credentials.forceRelay && relayFallbackRef.current
                    ? 'Phone online — relay unreachable, using direct connection'
                    : 'Phone online',
                  remoteIdentity: null,
                }));
              // Publish 'online' on the lease record immediately so a lame-duck
              // loser tab finishes its handoff without waiting for a renew tick.
              if (hasRuntimeLeaseRef.current) {
                writeSoftphoneOwnerLease({ ...buildOwnerLease(), phase: 'online' });
              }
              return;
            }
            if (registerState === sip.RegistererState.Unregistered) {
              markPhoneRegistered(false);
              void recordTelemetrySessionEvent('registration_state', 'warning', { state: 'unregistered' }, telemetryRef.current.activeCallId);
              // A stuck-media rebuild un-REGISTERed on purpose and re-REGISTERs itself.
              if (mediaRebuildInFlightRef.current) {
                return;
              }
              // NET-227: with a live call, re-register in place (reconnectInPlace)
              // rather than tearing the runtime down.
              if (hasRuntimeLeaseRef.current && hasLiveSession()) {
                void reconnectInPlace();
                return;
              }
              setState((current) => ({
                ...current,
                phase: 'connecting',
                extension: assignedExtension,
                message: 'Reconnecting phone...',
                remoteIdentity: null,
              }));
              scheduleReconnect();
            }
          });

          return registerer;
        };
        createRegistererRef.current = createRegisterer;
        const registerer = createRegisterer(userAgent);

        runtimeRef.current.userAgent = userAgent;
        runtimeRef.current.registerer = registerer;
        runtimeRef.current.session = null;
        setPoolLineLive(false);

        await withTimeout(userAgent.start(), SOFTPHONE_REGISTER_TIMEOUT_MS, 'SIP transport start timed out');
        // No Contact:* Expires:0 "reset" before registering. The internal profile is
        // single-registration (multiple-registrations=false), so this REGISTER already
        // replaces any stale binding atomically — whereas a reset unregisters the
        // extension (including another tab's live phone) with no guarantee the
        // follow-up register() ever succeeds.
        await withTimeout(registerer.register(), SOFTPHONE_REGISTER_TIMEOUT_MS, 'SIP registration timed out');
      } catch (error) {
        if (isCancelled()) {
          return;
        }
        // A deploy that replaced the sip.js chunk leaves this tab unable to
        // build a phone at all, and a caught rejection never reaches the
        // unhandledrejection backstop — a hard reload is the only recovery,
        // which is what the floor has been doing by hand.
        //
        // A reload is destructive here in a way the app-shell's own reload is
        // not: it bypasses releaseLineForReload, so a setter parked on a pool
        // line would lose the leg while the server still holds their seat. It
        // is gated on this tab having no phone runtime at all — no UserAgent
        // means sip.js never built anything, so there is no registration, no
        // parked leg and no call. That is exactly the state a chunk failure
        // implies (sip.js is the only dynamic import in this try, and the
        // memoized loader keeps a successful load forever), but the gate makes
        // it structural rather than something a later edit could quietly break.
        //
        // False means "keep handling this yourself" — not a chunk error, or a
        // reload the seam declined (offline, or already reloaded once) — so it
        // falls through to the normal failure path below, which reports the
        // phone and schedules the retry.
        if (!hasPhoneRuntime(runtimeRef.current, hasLiveSession()) && reloadIfChunkLoadError(error)) {
          return;
        }

        markPhoneRegistered(false);
        const message = error instanceof Error ? error.message : 'Failed to start browser phone';
        void recordTelemetrySessionEvent('runtime_start_failed', 'error', { message }, telemetryRef.current.activeCallId);
        // A retry is always scheduled from here, so the phone is reconnecting,
        // not failed; the registration clock says when that has gone on too long.
        setState({
          phase: 'connecting',
          extension: assignedExtension,
          message: 'Reconnecting phone...',
          remoteIdentity: null,
        });
        scheduleReconnect();
      }
    }

    void startPhone();

    return () => {
      cancelled = true;
      // NET-227: if the effect re-runs while an in-place reconnect is healing a
      // live call we still hold the lease for, preserve the runtime so the call
      // survives. Any other re-run reason (lease loss, role change, unmount) takes
      // the full teardown — never preserve when the lease is gone (double-call risk).
      const preserveSession =
        reconnectInFlightRef.current
        && hasRuntimeLeaseRef.current
        && hasLiveSession();
      if (!preserveSession) {
        // Make-before-break: when this run ends because the lease moved to a
        // live tab, keep the runtime — the effect's no-lease branch starts the
        // lame-duck handoff. Every other reason (retry nonce, role change,
        // unmount — where the page is going away regardless) tears down here.
        const owner = readSoftphoneOwnerLease();
        const lostLeaseToLiveTab = Boolean(
          owner
          && owner.tabId !== tabIdRef.current
          && (Date.now() - owner.updatedAt) <= SOFTPHONE_OWNER_TTL_MS
        );
        if (lostLeaseToLiveTab && runtimeRef.current.userAgent && !runtimeRef.current.stopped) {
          return;
        }
      }
      void stopRuntime({ endTelemetry: !preserveSession, preserveSession });
    };
  }, [
    applyDeviceError,
    attachPeerConnectionTelemetry,
    beginLeaseHandoff,
    buildOwnerLease,
    cancelLeaseHandoff,
    enabledForRole,
    beginTelemetrySession,
    flushPeerConnectionSample,
    hasLiveSession,
    isLameDuck,
    markPhoneRegistered,
    recordTelemetrySessionEvent,
    reconnectInPlace,
    resetAudioElement,
    hasRuntimeLease,
    retryNonce,
    scheduleReconnect,
    stopRuntime,
    stopTone,
    stopTelemetryStats,
  ]);

  useEffect(() => {
    if (!enabledForRole || !hasRuntimeLease || !telemetryRef.current.sessionId) {
      return;
    }
    telemetryRef.current.activeCallId = activeCall?.callId || null;
  }, [activeCall?.callId, enabledForRole, hasRuntimeLease]);

  // Registration for the bars. Only the tab holding the runtime lease has a
  // registerer of its own, so anywhere else this reads the owner's mirrored
  // verdict rather than inventing one. The lease is the whole gate on purpose:
  // a retry cycle tears the runtime down and rebuilds it under the same lease,
  // and the agent has been unable to call for that entire stretch, so the clock
  // must keep running across it. This only ever reads the clock — stamping it
  // belongs at the transition (markPhoneRegistered), because a tick that skips
  // while the tab is hidden would otherwise restart the grace window at the
  // moment the agent comes back to look at the widget.
  const sampleRegistration = useCallback((): RegistrationSnapshot => {
    if (!hasRuntimeLeaseRef.current) {
      // A lame duck holds no lease but still IS the phone, and applyBusSnapshot
      // pointedly does not overwrite its mirror — so speak for its own binding
      // rather than reading a verdict about somebody else's.
      if (isLameDuck()) {
        return phoneRegisteredRef.current ? { status: 'registered' } : { status: 'unknown' };
      }
      // Not the phone, so this tab has no registration of its own to report.
      // markPhoneRegistered has already seen to it that there is no clock here
      // to go stale either.
      const mirrored = mirroredRegistrationRef.current;
      return mirrored ? { status: 'mirrored', ...mirrored } : { status: 'unknown' };
    }
    if (phoneRegisteredRef.current) {
      return { status: 'registered' };
    }
    const downSince = reportableRegistrationClock({
      downSinceMs: registrationDownSinceRef.current,
      deviceFault: deviceErrorRef.current !== null,
      seatDisabled: latestStateRef.current.phase === 'disabled',
    });
    if (downSince === null) {
      return { status: 'unknown' };
    }
    return {
      status: 'unregistered',
      forMs: Date.now() - downSince,
      // 'connecting' covers the runtime's own start attempt, which holds no
      // timer of its own and can run to SOFTPHONE_REGISTER_TIMEOUT_MS before the catch
      // schedules the next one.
      retrying: reconnectTimerRef.current !== null
        || reconnectInFlightRef.current
        || latestStateRef.current.phase === 'connecting',
    };
  }, [isLameDuck]);

  // Connection-quality bars in the softphone widget: the browser's network
  // estimate at all times plus the live media path while a call is up.
  // Refreshed on network events and a slow tick, visible tab only.
  useEffect(() => {
    if (!enabledForRole) {
      return undefined;
    }
    let cancelled = false;
    const isCancelled = () => cancelled;
    const refresh = async () => {
      if (isCancelled()) {
        return;
      }
      // Registration is graded ahead of the visibility gate, and deliberately:
      // in a two-tab setup the owner is usually the tab that is NOT in front,
      // and mirrors render from the verdict it publishes. Gating this too would
      // freeze that verdict at whatever it was when the owner lost focus and
      // leave the visible mirror green right through an outage. It needs no
      // getStats and no visible document, so there is nothing to gate.
      const registration = sampleRegistration();
      const verdict = registrationVerdict(registration);
      setRegistrationFault((current) => (
        current.down === verdict.down && current.retrying === verdict.retrying ? current : verdict
      ));
      if (document.visibilityState !== 'visible') {
        return;
      }
      const session = runtimeRef.current.session as (Session & {
        sessionDescriptionHandler?: { peerConnection?: RTCPeerConnection };
      }) | null;
      const peerConnection = session?.sessionDescriptionHandler?.peerConnection;
      const livePeerConnection = peerConnection?.connectionState === 'connected' ? peerConnection : null;
      let call: CallLinkQuality | null = null;
      if (livePeerConnection) {
        const counters = await readCallLinkCounters(livePeerConnection).catch(() => null);
        if (counters) {
          call = callLinkCountersRef.current ? callLinkQualityBetween(callLinkCountersRef.current, counters) : null;
          callLinkCountersRef.current = counters;
        }
      } else {
        callLinkCountersRef.current = null;
      }
      if (isCancelled()) {
        return;
      }
      const network = readNetworkSnapshot();
      setConnectionQuality((current) => {
        const next = rateConnection({
          online: network.online ?? navigator.onLine,
          transportDown: reconnecting,
          registration,
          rttMs: network.rttMs ?? null,
          downlinkMbps: network.downlinkMbps ?? null,
          // A short window has no reading of its own; keep the last one while the call lasts.
          call: call ?? (livePeerConnection ? current.call : null),
          reconnects: transportDropsRef.current,
        });
        return connectionQualitySignature(current) === connectionQualitySignature(next) ? current : next;
      });
    };
    void refresh();
    const timer = window.setInterval(() => {
      void refresh();
    }, CONNECTION_QUALITY_TICK_MS);
    const connection = networkInformation();
    const onEvent = () => {
      void refresh();
    };
    window.addEventListener('online', onEvent);
    window.addEventListener('offline', onEvent);
    document.addEventListener('visibilitychange', onEvent);
    connection?.addEventListener?.('change', onEvent);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      window.removeEventListener('online', onEvent);
      window.removeEventListener('offline', onEvent);
      document.removeEventListener('visibilitychange', onEvent);
      connection?.removeEventListener?.('change', onEvent);
    };
  }, [enabledForRole, reconnecting, sampleRegistration, state.phase]);

  useEffect(() => {
    if (!enabledForRole || !hasRuntimeLease) {
      return undefined;
    }

    const handleOnlineState = () => {
      if (window.navigator.onLine) {
        resumeReconnectNow();
      }
      void recordTelemetrySessionEvent(window.navigator.onLine ? 'browser_online' : 'browser_offline', window.navigator.onLine ? 'info' : 'warning', {
        network: readNetworkSnapshot(),
      });
    };

    const handleVisibilityState = () => {
      // Coming back to a tab that has been backgrounded through an outage:
      // don't make the agent wait out a capped backoff interval as well.
      if (document.visibilityState === 'visible') {
        resumeReconnectNow();
      }
      void recordTelemetrySessionEvent('visibility_change', 'info', {
        visibilityState: document.visibilityState,
      });
    };

    window.addEventListener('online', handleOnlineState);
    window.addEventListener('offline', handleOnlineState);
    document.addEventListener('visibilitychange', handleVisibilityState);

    return () => {
      window.removeEventListener('online', handleOnlineState);
      window.removeEventListener('offline', handleOnlineState);
      document.removeEventListener('visibilitychange', handleVisibilityState);
    };
  }, [enabledForRole, hasRuntimeLease, recordTelemetrySessionEvent, resumeReconnectNow]);

  useEffect(() => {
    // Owner only: mirror tabs receive the same phases over the bus, and
    // letting each of them start an oscillator would ring once per open tab
    // through the same output device.
    if (!hasRuntimeLease) {
      stopTone();
      return;
    }
    // An incoming call rings whether it holds the primary slot (phase 'ringing')
    // or is waiting behind an active call (incomingCall banner).
    if (state.phase === 'ringing' || incomingCall) {
      void startTone('ring');
      return;
    }
    if (activeCall?.status === 'originating' && activeCall.source !== 'inbound') {
      void startTone('ringback');
      return;
    }
    stopTone();
  }, [activeCall?.source, activeCall?.status, hasRuntimeLease, incomingCall, startTone, state.phase, stopTone]);

  useEffect(() => {
    if (!enabledForRole || !hasRuntimeLease) {
      return undefined;
    }

    const shouldReconcileActiveCall =
      Boolean(activeCall)
      || state.phase === 'in_call'
      || state.phase === 'hot_transfer_pending'
      || (state.phase === 'calling' && phoneRegisteredRef.current);

    if (!shouldReconcileActiveCall) {
      return undefined;
    }

    let disposed = false;
    let running = false;
    let rerunRequested = false;

    async function reconcile(): Promise<void> {
      if (running) {
        rerunRequested = true;
        return;
      }
      running = true;
      try {
        const payload = await fetchJson<{ ok: true; calls: CallSummary[] }>(ACTIVE_CALL_SNAPSHOT_PATH);
        if (disposed) {
          return;
        }
        publishRecentCalls(payload.calls);
        const currentCall = pickCurrentUserCall(payload.calls, state.extension, activeCallRef.current, sessionSalesCallIdRef.current);
        if (!currentCall) {
          clearAbsentCallUi(payload.calls);
          return;
        }
        // Unchanged content → keep the existing object identity so nothing
        // downstream re-renders (and this effect's own deps stay stable, so the
        // interval isn't torn down and recreated every tick).
        if (isSameCallSnapshot(activeCallRef.current, currentCall)) {
          return;
        }
        activeCallRef.current = currentCall;
        setActiveCall(currentCall);
      } catch {
        // Ignore transient reconcile failures.
      } finally {
        running = false;
        if (!disposed && rerunRequested) {
          rerunRequested = false;
          void reconcile();
        }
      }
    }

    // The stream pushes every transition for this call; only reconcile over
    // HTTP when it has gone quiet (half-open socket, missed reconnect).
    const reconcileIfStale = () => {
      if (isStreamFresh()) {
        return;
      }
      void reconcile();
    };
    reconcileIfStale();
    const interval = window.setInterval(reconcileIfStale, STREAM_ACTIVE_RECONCILE_POLL_MS);

    return () => {
      disposed = true;
      window.clearInterval(interval);
    };
  }, [activeCall, clearAbsentCallUi, enabledForRole, hasRuntimeLease, isStreamFresh, publishRecentCalls, state.extension, state.phase]);

  // oxlint-disable-next-line max-lines-per-function -- frozen baseline (structural-gates rollout)
  useEffect(() => {
    if (!enabledForRole || !hasRuntimeLease) {
      activeCallRef.current = null;
      setActiveCall(null);
      publishRecentCalls([]);
      return undefined;
    }

    let cancelled = false;
    let stream: EventSource | null = null;
    let pollingTimer: number | null = null;
    let reconcileTimer: number | null = null;
    let reconnectTimer: number | null = null;
    let pollingInFlight = false;
    let pollingQueued = false;
    // Delta-protocol mirror of the server's per-connection visible call set.
    // Effect-scoped (fresh per run) and shared via the ref only so forced
    // refreshes can rebaseline it — never React state (PR #476).
    const mirror = createCallStreamMirror();
    callStreamMirrorRef.current = mirror;

    function clearReconnect(): void {
      if (reconnectTimer !== null) {
        window.clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
    }

    function stopPolling(): void {
      if (pollingTimer !== null) {
        window.clearInterval(pollingTimer);
        pollingTimer = null;
      }
    }

    function stopReconcilePolling(): void {
      if (reconcileTimer !== null) {
        window.clearInterval(reconcileTimer);
        reconcileTimer = null;
      }
    }

    function applyCallSnapshot(calls: CallSummary[]): void {
      publishRecentCalls(calls);
      // pickCurrentUserCall filters ended calls itself, so the mixed
      // (active + ended) tracked snapshot is safe to feed straight through.
      const currentCall = pickCurrentUserCall(calls, state.extension, activeCallRef.current, sessionSalesCallIdRef.current);
      // Only publish a new object when the content actually changed — see
      // isSameCallSnapshot for why identity churn here is so expensive.
      if (!isSameCallSnapshot(activeCallRef.current, currentCall)) {
        activeCallRef.current = currentCall;
        setActiveCall(currentCall);
      }

      if (!currentCall) {
        clearAbsentCallUi(calls);
        return;
      }

      setState((current) => {
        // Unanswered inbound legs are driven by the SIP invite (ringing UI),
        // never by server snapshots — a tracked inbound call is not "dialing".
        if (current.phase === 'ringing') {
          return current;
        }
        if (currentCall.status === 'originating' || currentCall.status === 'answered') {
          if (currentCall.source === 'inbound') {
            return current;
          }
          const destinationLabel = currentCall.destination || currentCall.toPhone || 'destination';
          const nextMessage = `Dialing ${destinationLabel}...`;
          if (current.phase === 'calling' && current.message === nextMessage) {
            return current;
          }
          return {
            ...current,
            phase: 'calling',
            message: nextMessage,
            remoteIdentity: null,
          };
        }

        if (currentCall.status === 'bridged' && current.phase === 'in_call') {
          return current;
        }
        if (currentCall.status === 'bridged') {
          return {
            ...current,
            phase: 'in_call',
            message: 'Call connected',
          };
        }
        return current;
      });
    }

    async function pollCallsOnce(): Promise<void> {
      if (pollingInFlight) {
        pollingQueued = true;
        return;
      }
      pollingInFlight = true;
      try {
        const payload = await fetchJson<{ ok: true; calls: CallSummary[] }>(ACTIVE_CALL_SNAPSHOT_PATH);
        if (cancelled) {
          return;
        }
        // Full-refresh path doubles as the delta recovery lever: rebaseline
        // the mirror (content only — a poll knows nothing about sequencing).
        mirror.applyFull(payload.calls);
        applyCallSnapshot(payload.calls);
      } catch {
        // Ignore transient fallback polling failures.
      } finally {
        pollingInFlight = false;
        if (!cancelled && pollingQueued) {
          pollingQueued = false;
          void pollCallsOnce();
        }
      }
    }

    function startPolling(): void {
      if (pollingTimer !== null) {
        return;
      }
      pollingTimer = window.setInterval(() => {
        void pollCallsOnce();
      }, STREAM_FALLBACK_POLL_MS);
      void pollCallsOnce();
    }

    function startReconcilePolling(): void {
      if (reconcileTimer !== null) {
        return;
      }
      // Insurance against a silently dead stream, not a steady-state poll:
      // skipped entirely while events (or pings) are arriving.
      reconcileTimer = window.setInterval(() => {
        if (isStreamFresh()) {
          return;
        }
        void pollCallsOnce();
      }, STREAM_RECONCILE_POLL_MS);
    }

    function handleStreamPayload(raw: string): void {
      try {
        const payload = JSON.parse(raw) as { calls?: CallSummary[]; seq?: number };
        if (!Array.isArray(payload.calls)) {
          return;
        }
        // Full snapshots ("snapshot" on subscribe, periodic 60s resyncs)
        // rebaseline the mirror; `seq` is present exactly when the server is
        // in delta mode (a legacy backend during version skew sends none).
        mirror.applyFull(payload.calls, typeof payload.seq === 'number' ? payload.seq : undefined);
        applyCallSnapshot(mirror.toSortedCalls());
      } catch {
        // Ignore malformed stream chunks.
      }
    }

    function handleDeltaPayload(raw: string): void {
      let applied: { ok: boolean; calls?: CallSummary[] };
      try {
        const payload = JSON.parse(raw) as Partial<CallStreamDeltaPayload>;
        if (typeof payload.seq !== 'number' || !Array.isArray(payload.changed) || !Array.isArray(payload.removed)) {
          applied = { ok: false };
        } else {
          applied = mirror.applyDelta(payload as CallStreamDeltaPayload);
        }
      } catch {
        applied = { ok: false };
      }
      if (!applied.ok || !applied.calls) {
        // Malformed or out-of-sequence delta: degrade to the existing
        // full-refresh machinery, which rebaselines the mirror on return.
        void pollCallsOnce();
        return;
      }
      applyCallSnapshot(applied.calls);
    }

    function connectStream(): void {
      if (cancelled) {
        return;
      }
      if (stream) {
        stream.close();
      }

      // The server always speaks the delta protocol and ignores proto=delta;
      // it is still sent because older backends (a rollback) only send deltas
      // to connections that ask for them.
      const source = new EventSource('/api/sip/stream?proto=delta');
      stream = source;

      const handleSnapshot = (event: MessageEvent): void => {
        if (cancelled) {
          return;
        }
        streamLastEventAtRef.current = Date.now();
        renewOwnerLeaseFromStream();
        handleStreamPayload(event.data);
      };

      const handleDelta = (event: MessageEvent): void => {
        if (cancelled) {
          return;
        }
        streamLastEventAtRef.current = Date.now();
        renewOwnerLeaseFromStream();
        handleDeltaPayload(event.data);
      };

      source.addEventListener('snapshot', handleSnapshot);
      source.addEventListener('calls', handleSnapshot);
      source.addEventListener('delta', handleDelta);
      // Liveness only — lets isStreamFresh distinguish a quiet-but-healthy
      // stream from a half-open socket, so the reconcile timers can stay idle.
      // Also rides the lease renewal: server pings arrive every 15s and, unlike
      // this tab's timers, are never throttled while hidden (see
      // renewOwnerLeaseFromStream).
      source.addEventListener('ping', () => {
        if (!cancelled) {
          streamLastEventAtRef.current = Date.now();
          renewOwnerLeaseFromStream();
        }
      });

      source.onopen = () => {
        if (cancelled) {
          return;
        }
        streamOpenRef.current = true;
        streamLastEventAtRef.current = Date.now();
        clearReconnect();
        stopPolling();
      };

      source.onerror = () => {
        if (cancelled) {
          return;
        }
        streamOpenRef.current = false;
        if (stream === source) {
          stream = null;
        }
        source.close();
        startPolling();
        reconnectTimer ??= window.setTimeout(() => {
          reconnectTimer = null;
          connectStream();
        }, STREAM_RECONNECT_MS);
      };
    }

    startReconcilePolling();
    void pollCallsOnce();
    connectStream();

    return () => {
      cancelled = true;
      streamOpenRef.current = false;
      if (callStreamMirrorRef.current === mirror) {
        callStreamMirrorRef.current = null;
      }
      clearReconnect();
      stopPolling();
      stopReconcilePolling();
      stopTone();
      if (stream) {
        stream.close();
        stream = null;
      }
    };
  }, [clearAbsentCallUi, enabledForRole, hasRuntimeLease, isStreamFresh, publishRecentCalls, renewOwnerLeaseFromStream, state.extension, stopTone]);

  // Context-facing telemetry writer: the telemetry session lives with the
  // runtime, so events raised from a mirror tab (e.g. hot-transfer UI) are
  // forwarded to the owner instead of being dropped for lack of a session.
  const recordTelemetryEventFromAnyTab = useCallback(async (
    eventName: string,
    severity: 'info' | 'warning' | 'error' = 'info',
    payload: Record<string, unknown> = {},
    callId?: string | null
  ): Promise<void> => {
    if (!hasRuntimeLeaseRef.current) {
      postOwnerCommand({ type: 'telemetry', eventName, severity, payload, callId: callId ?? null });
      return;
    }
    await recordTelemetrySessionEvent(eventName, severity, payload, callId);
  }, [postOwnerCommand, recordTelemetrySessionEvent]);

  // Any tab may ask; only the owner holds a registerer.
  const reRegister = useCallback((reason: string) => {
    if (!hasRuntimeLeaseRef.current) {
      postOwnerCommand({ type: 'reregister', reason });
      return;
    }
    reRegisterNow(reason);
  }, [postOwnerCommand, reRegisterNow]);

  // Agent-initiated full restart of the phone, skipping any backoff wait.
  const reconnect = useCallback(() => {
    if (!hasRuntimeLeaseRef.current) {
      postOwnerCommand({ type: 'reconnect' });
      return;
    }
    void recordTelemetrySessionEvent('manual_reconnect', 'info', {});
    restartPhoneNow();
  }, [postOwnerCommand, recordTelemetrySessionEvent, restartPhoneNow]);

  return {
    enabledForRole,
    // True when this tab owns the phone runtime (SIP registration + audio).
    // Mirror tabs read live state but must not drive call-originating loops —
    // the automatic dialer gates on this.
    hasRuntimeLease,
    poolLineLive,
    state: presentedState,
    primaryDidNumber,
    activeCall,
    recentCalls,
    refreshCallSnapshot,
    localAudioMuted,
    setLocalAudioMuted,
    setAudioInputDevice,
    setAudioOutputDevice,
    reconnecting,
    mediaFailed,
    silentUplink,
    connectionQuality,
    dialDestination,
    setDialDestination,
    matchedContact,
    matchedContactStatus,
    actionError,
    clearActionError,
    syncIdentityForCampaign,
    dial,
    remoteAudioRef,
    answer,
    reject,
    hangup,
    incomingCall,
    acceptIncomingCall,
    declineIncomingCall,
    sendDtmf,
    recordTelemetryEvent: recordTelemetryEventFromAnyTab,
    reRegister,
    reconnect,
  };
}
